import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  FileUp,
  FileText,
  Loader2,
  RefreshCw,
  ScanLine,
  Search,
  Sparkles,
} from 'lucide-react';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import { useSession } from '@/lib/auth';
import { useShortcutHint } from '@/app/StatusLine';
import type { CountQuestion, PaginatedResponse, ProductDetail } from '@/lib/api/types';
import {
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { ChipGroup, Pagination } from '@/ui/Filters';
import {
  Checkbox,
  Field,
  Input,
  MoneyInput,
  QuantityInput,
  Select,
  Textarea,
} from '@/ui/Field';
import { Callout } from '@/ui/Callout';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { cn } from '@/lib/cn';
import { countLabel, money, num, qty, relativeTime, smartDate, time } from '@/lib/format';
import { checkQuantity } from '@/lib/quantity';
import {
  INVOICE_NUMBER_MAX_LENGTH,
  INVOICE_NUMBER_TOO_LONG_MESSAGE,
  quantityText,
  type QuantityUnit,
} from '@kk-pos/shared/purchasing';
import { printedVendorSku } from '@kk-pos/shared/invoice-enrichment';
import {
  buildConfirmPayload,
  caseModeSubtotalCents,
  createProductsForLines,
  derivedPerEachCents,
  dollarsToCents,
  handMatchEnrichment,
  importKeys,
  invalidateAfterReceive,
  isGuessedMatch,
  itemNumberConflict,
  keepMarginPriceCents,
  poKeys,
  productFieldsForLine,
  remainderToastAction,
  submitPayloadForLine,
} from '@/features/buying/api';
import { costDecisionSummary } from '@/features/buying/cost-decisions';
import {
  checkInvoiceTotals,
  printedCents,
  totalsExplanation,
  totalsSummary,
} from '@/features/buying/invoice-totals';
import { totalsByUnit, unitTotalsText } from '@/features/buying/unit-totals';
import type { BulkCreateLine } from '@/features/buying/api';
import {
  COUNT_SOURCE_LABEL,
  DELIVERY_DATE_REQUIRED,
  DELIVERY_DATE_WINDOW_DAYS,
  SameDayChoice,
  addDays,
  answersToSend,
  countAnswerKey,
  countConfirmationConsequence,
  countQuestionsFromError,
  localToday,
  refusalCode,
  stockAnswer,
  storeDayLabel,
  unansweredMessage,
  useCountCheck,
} from '@/features/buying/CountConfirmation';
import {
  DuplicateInvoiceCallout,
  notesWithDuplicateAnswer,
  useDuplicateInvoiceCheck,
} from '@/features/buying/duplicate-invoice';
import { InvoiceFilePane } from '@/features/buying/InvoiceFilePane';
import { CreateProductDialog } from '@/features/buying/CreateProductDialog';
import {
  openOrdersFromError,
  recordedOnOrderFromError,
  codeOf,
  detailsOf,
  useOrderCandidates,
} from '@/features/buying/invoice/api';
import { WhichOrderStep } from '@/features/buying/invoice/WhichOrderStep';
import { PurchasingTabs } from '@/features/buying/PurchasingTabs';
import { dayLabel } from '@/features/buying/order-guide/labels';
import type { OpenOrderChoice } from '@/features/buying/invoice/types';
import {
  DismissDialog,
  DismissedPanel,
  dismissReasonLabel,
  useRestoreImport,
} from '@/features/buying/import-dismissal';
import {
  ConfidenceBadge,
  ImportStatusBadge,
  IMPORT_STATUS_FILTERS,
  ProductPicker,
  VendorSelect,
  WarningList,
  importStatusLabel,
  useVendorOptions,
} from '@/features/buying/parts';
import {
  NEEDS_REASON_LABEL,
  REVIEWING_FRESH_MS,
  actionableWarnings,
  costMovePct,
  foldingFor,
  isTypingOrDialog,
  lineNeeds,
  previousReviewerText,
  reviewingText,
  stepLine,
  unfoldedNote,
  type NeedsReason,
} from '@/features/buying/needs-you';
import { reviewableImport, useReviewingMarker } from '@/features/buying/reviewing-marker';
import type {
  ConfirmInvoiceResult,
  CreatedProduct,
  ImportStatus,
  InactiveMatch,
  MatchedLine,
  InvoiceImportDetail,
  InvoiceImportRow,
  InvoiceUploadResult,
  MatchCandidate,
  MatchConfidence,
  ParserWarning,
  SavedReviewDraft,
  SavedReviewLine,
} from '@/features/buying/types';

const PAGE_SIZE = 50;
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic';
const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic'];

/** TASK-0244: the inbox filter: an import status, every import, or the ones that need a person. */
type InboxFilter = ImportStatus | 'all' | 'needs';
/** TASK-0244 review R1-3: how often the inbox's "Needs you" filter looks for newly read uploads. */
export const NEEDS_YOU_POLL_MS = 15_000;

/** TASK-0244: "Needs you" first, then the statuses. */
const INBOX_FILTERS: Array<{ value: InboxFilter; label: string }> = [
  { value: 'needs', label: 'Needs you' },
  ...IMPORT_STATUS_FILTERS,
];

function isImportStatus(value: unknown): value is InboxFilter {
  return typeof value === 'string' && INBOX_FILTERS.some((f) => f.value === value);
}

/**
 * `state`, not `status`: this route is a child of `/purchasing`, so it inherits
 * that route's search schema — and `status` there means a PO status. Reusing
 * the name would make the two enums collide.
 */
type ImportSearch = {
  id?: number;
  state?: InboxFilter;
  page?: number;
  vendorId?: number;
  /** TASK-0239: list the dismissed imports too (hidden by default). */
  dismissed?: boolean;
  /**
   * TASK-0250 (FR-TODAY): only imports made on or after this store day (`YYYY-MM-DD`), and only the
   * held-back parts of invoices (`followup`) or everything else (`original`). The Today rows link here
   * with the window their counts were taken over, so a row's count is this list's total.
   */
  since?: string;
  kind?: ImportKindFilter;
};

/** TASK-0250: held-back parts only, or every other import. */
type ImportKindFilter = 'followup' | 'original';

const STORE_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute('/_app/purchasing/import')({
  staticData: { crumb: 'Invoices' },
  validateSearch: (s: Record<string, unknown>): ImportSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    state: isImportStatus(s.state) ? s.state : 'all',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
    vendorId: Number(s.vendorId) > 0 ? Number(s.vendorId) : undefined,
    dismissed: s.dismissed === true || s.dismissed === 'true' ? true : undefined,
    since: typeof s.since === 'string' && STORE_DAY.test(s.since) ? s.since : undefined,
    kind: s.kind === 'followup' || s.kind === 'original' ? s.kind : undefined,
  }),
  component: InvoiceImportScreen,
});

function InvoiceImportScreen() {
  const raw = Route.useSearch();
  const search = {
    id: raw.id,
    state: raw.state ?? ('all' as InboxFilter),
    page: raw.page ?? 1,
    vendorId: raw.vendorId,
    dismissed: raw.dismissed === true,
    since: raw.since,
    kind: raw.kind,
  };
  const navigate = useNavigate({ from: Route.fullPath });
  const me = useSession()?.id ?? null;

  const list = useQuery({
    queryKey: importKeys.list({
      state: search.state,
      page: search.page,
      dismissed: search.dismissed,
      since: search.since,
      kind: search.kind,
    }),
    queryFn: () =>
      api.get<PaginatedResponse<InvoiceImportRow>>('/ai/invoice-imports', {
        params: {
          page: search.page,
          pageSize: PAGE_SIZE,
          ...(search.state === 'all'
            ? {}
            : search.state === 'needs'
              ? { needsYou: 'true' }
              : { status: search.state }),
          ...(search.dismissed ? { includeDismissed: 'true' } : {}),
          ...(search.since ? { createdAfter: search.since } : {}),
          ...(search.kind ? { kind: search.kind } : {}),
        },
      }),
    // A queued file becomes reviewable without any action from the operator,
    // so the inbox polls while anything is still in flight. TASK-0244: while a review is open it
    // also polls slowly, so "Dina is looking at this" shows up without a reload. "Needs you" never
    // lists a file still being read, so it polls too (review R1-3): a new upload appears once read.
    refetchInterval: (query) => {
      const rows = query.state.data?.data ?? [];
      if (rows.some((row) => row.status === 'queued' || row.status === 'parsing'))
        return 4000;
      if (search.state === 'needs') return NEEDS_YOU_POLL_MS;
      return search.id ? 30_000 : false;
    },
  });

  const rows = list.data?.data ?? [];
  const setSearch = (next: ImportSearch) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }) });

  return (
    <Page>
      <PageHeader
        eyebrow="Buying"
        title="Purchasing"
        subtitle="Upload supplier paperwork, review what the parser read, then turn it into a received purchase order"
        tabs={<PurchasingTabs />}
      />

      <PageBody bleed className="flex min-h-0">
        <aside className="border-line flex w-80 shrink-0 flex-col border-r">
          <UploadPanel
            vendorId={search.vendorId ?? null}
            onVendorChange={(vendorId) => setSearch({ vendorId: vendorId ?? undefined })}
          />

          <div className="border-line shrink-0 border-b px-2 py-2">
            <ChipGroup<InboxFilter>
              value={search.state}
              onChange={(state) => setSearch({ state, page: 1 })}
              options={INBOX_FILTERS.map((f) => ({
                value: f.value,
                label: f.label,
              }))}
            />
            <Checkbox
              className="mt-2 px-1"
              checked={search.dismissed}
              onCheckedChange={(dismissed) =>
                setSearch({ dismissed: dismissed ? true : undefined, page: 1 })
              }
              label={<span className="text-2xs text-ink-muted">Show dismissed</span>}
            />
            {search.since || search.kind ? (
              <InboxWindowNote
                since={search.since}
                kind={search.kind}
                onClear={() => setSearch({ since: undefined, kind: undefined, page: 1 })}
              />
            ) : null}
          </div>

          <div className="scrollpane min-h-0 flex-1">
            {list.isPending ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : list.isError ? (
              <ErrorState
                className="m-3"
                title="Could not load the inbox"
                message={errorMessage(list.error)}
                onRetry={() => void list.refetch()}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                compact
                icon={<ScanLine />}
                title={search.state === 'needs' ? 'Nothing needs you' : 'Nothing here'}
                description={
                  search.state === 'needs'
                    ? 'Every read invoice is recorded. New uploads show up here once they are read.'
                    : 'Upload an invoice above to get started.'
                }
              />
            ) : (
              rows.map((row) => (
                <button
                  key={row.id}
                  onClick={() => setSearch({ id: row.id })}
                  className={cn(
                    'border-line-soft flex w-full flex-col gap-1 border-b px-3 py-2 text-left',
                    row.id === search.id ? 'bg-signal-muted' : 'hover:bg-surface-hover',
                    row.dismissedAt && 'opacity-70'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-ink min-w-0 flex-1 truncate text-xs">
                      {row.sourceFilename ?? `Import #${row.id}`}
                    </span>
                    {row.dismissedAt ? (
                      <Badge>Dismissed</Badge>
                    ) : (
                      <ImportStatusBadge status={row.status} />
                    )}
                  </div>
                  <div className="text-2xs text-ink-faint flex items-center gap-2">
                    <span className="truncate">{row.vendorName ?? 'Vendor unset'}</span>
                    <span className="ml-auto shrink-0 font-mono tabular-nums">
                      {relativeTime(row.createdAt)}
                    </span>
                  </div>
                  {row.dismissedAt ? (
                    <div className="text-2xs text-ink-muted truncate">
                      {dismissReasonLabel(row.dismissedReason)}
                      {row.dismissedNote ? ` · ${row.dismissedNote}` : ''}
                    </div>
                  ) : null}
                  {row.purchaseOrderId ? (
                    <div className="text-2xs text-positive font-mono">
                      → PO #{row.purchaseOrderId}
                    </div>
                  ) : null}
                  <InboxRowNotes row={row} me={me} />
                  {row.parseError ? (
                    <div className="text-2xs text-negative truncate">
                      {row.parseError}
                    </div>
                  ) : null}
                </button>
              ))
            )}
          </div>

          <div className="border-line shrink-0 border-t px-3 py-1.5">
            <Pagination
              page={search.page}
              pageSize={PAGE_SIZE}
              total={list.data?.total ?? 0}
              onPageChange={(page) => setSearch({ page })}
            />
          </div>
        </aside>

        <div className="scrollpane min-h-0 flex-1">
          {search.id ? (
            <ReviewPane
              key={search.id}
              importId={search.id}
              inboxRow={rows.find((row) => row.id === search.id) ?? null}
              onDismissed={() => {
                // Listed dismissed imports stay selected (their pane offers Restore);
                // otherwise the import has left the list, so leave its pane too.
                if (!search.dismissed) setSearch({ id: undefined });
              }}
            />
          ) : (
            <EmptyState
              icon={<ScanLine />}
              title="No invoice selected"
              description="Pick one from the inbox, or upload a new file. Parsing runs in the background — you can leave this page."
            />
          )}
        </div>
      </PageBody>
    </Page>
  );
}

/**
 * TASK-0244 (FR-NEEDS): under an inbox row, who has it open ("Motti is looking at this, 3m ago"), and
 * what it waits on: aimed at a PO but not recorded there, or the "which PO?" step.
 */
function InboxRowNotes({ row, me }: { row: InvoiceImportRow; me: number | null }) {
  const open = !row.confirmed && row.status === 'matched' && !row.dismissedAt;
  const reviewing = open ? reviewingText(row, me, (iso) => relativeTime(iso)) : null;
  const aimed =
    open && row.targetPurchaseOrderId != null && row.vendorInvoiceId == null
      ? row.targetPurchaseOrderId
      : null;
  if (!reviewing && aimed === null && !(open && row.waitingOnOrder)) return null;
  const someoneElse =
    reviewing !== null &&
    row.reviewingByUserId !== me &&
    row.reviewingAt != null &&
    Date.now() - Date.parse(row.reviewingAt) < REVIEWING_FRESH_MS;
  return (
    <div className="text-2xs flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {aimed !== null ? (
        <span className="text-warn">For PO #{aimed}: waiting, open it</span>
      ) : open && row.waitingOnOrder ? (
        <span className="text-warn">Which PO? This vendor has an open order</span>
      ) : null}
      {reviewing ? (
        <span
          className={cn(
            'flex items-center gap-1',
            someoneElse ? 'text-signal-ink' : 'text-ink-faint'
          )}
        >
          <Eye className="size-3 shrink-0" aria-hidden />
          {reviewing}
        </span>
      ) : null}
    </div>
  );
}

/* ── Upload ────────────────────────────────────────────────────────────────── */

type UploadReport = {
  queued: number;
  duplicate: number;
  failed: number;
  notes: string[];
};

function UploadPanel({
  vendorId,
  onVendorChange,
}: {
  vendorId: number | null;
  onVendorChange: (vendorId: number | null) => void;
}) {
  const queryClient = useQueryClient();
  const vendors = useVendorOptions();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<UploadReport | null>(null);

  const upload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setBusy(true);
      const result: UploadReport = { queued: 0, duplicate: 0, failed: 0, notes: [] };

      for (const file of files) {
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        if (!ALLOWED_EXT.includes(ext)) {
          result.failed += 1;
          result.notes.push(`${file.name}: .${ext} is not an accepted file type.`);
          continue;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
          result.failed += 1;
          result.notes.push(`${file.name}: larger than the 50 MB limit.`);
          continue;
        }
        const body = new FormData();
        body.append('file', file, file.name);
        try {
          const res = await api.post<InvoiceUploadResult>('/ai/invoice-imports', body, {
            params: vendorId ? { vendorId } : undefined,
            timeout: 120_000,
          });
          // A 200 with duplicate:true means the sha256 was already uploaded —
          // it is NOT a new import and must not be counted as one.
          if (res.duplicate) {
            result.duplicate += 1;
            result.notes.push(
              res.dismissedAt
                ? `${file.name}: already uploaded and dismissed — turn on “Show dismissed” to restore import #${res.id}.`
                : `${file.name}: already uploaded — see import #${res.id}.`
            );
          } else {
            result.queued += 1;
          }
        } catch (error) {
          result.failed += 1;
          result.notes.push(`${file.name}: ${errorMessage(error)}`);
        }
      }

      setBusy(false);
      setReport(result);
      void queryClient.invalidateQueries({ queryKey: importKeys.all });

      const parts: string[] = [];
      if (result.queued) parts.push(`${result.queued} queued`);
      if (result.duplicate) parts.push(`${result.duplicate} already uploaded`);
      if (result.failed) parts.push(`${result.failed} failed`);
      const summary = parts.join(', ') || 'nothing to do';
      if (result.failed > 0) toast.error(`Upload finished: ${summary}.`);
      else toast.success(`Upload finished: ${summary}.`);
    },
    [queryClient, vendorId]
  );

  return (
    <div className="border-line shrink-0 border-b p-3">
      <Field
        label="Upload for vendor"
        hint="Optional — it scopes product matching. The parser can also detect the vendor itself."
      >
        <VendorSelect
          size="sm"
          value={vendorId}
          vendors={vendors.data}
          loading={vendors.isPending}
          placeholder="Let the parser decide"
          onChange={onVendorChange}
        />
      </Field>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void upload(Array.from(e.dataTransfer.files));
        }}
        className={cn(
          'mt-3 flex flex-col items-center gap-1.5 rounded border border-dashed p-4 text-center transition-colors',
          dragging ? 'border-signal bg-signal-muted' : 'border-line bg-surface-sunken'
        )}
      >
        {busy ? (
          <Loader2 className="text-signal size-4 animate-spin" />
        ) : (
          <FileUp className="text-ink-faint size-4" />
        )}
        <div className="text-2xs text-ink-muted">
          Drop invoices here, or
          <button
            className="text-signal-ink ml-1 hover:underline"
            onClick={() => inputRef.current?.click()}
          >
            browse
          </button>
        </div>
        <div className="text-2xs text-ink-faint">
          PDF, JPG, PNG, WebP or HEIC · up to 50 MB
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            e.currentTarget.value = '';
            void upload(files);
          }}
        />
      </div>

      {report ? (
        <div className="border-line bg-surface text-2xs mt-2 rounded border p-2">
          <div className="flex items-center gap-2">
            <span className="text-positive">{report.queued} queued</span>
            <span className="text-ink-muted">{report.duplicate} duplicate</span>
            <span className={report.failed > 0 ? 'text-negative' : 'text-ink-faint'}>
              {report.failed} failed
            </span>
            <button
              className="text-ink-faint hover:text-ink ml-auto"
              onClick={() => setReport(null)}
            >
              Dismiss
            </button>
          </div>
          {report.notes.length > 0 ? (
            <ul className="text-ink-muted mt-1.5 space-y-0.5">
              {report.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ── Review ────────────────────────────────────────────────────────────────── */

type ReviewLine = {
  key: number;
  description: string;
  sku: string;
  upc: string;
  /**
   * Raw text of the Received / Cases in box (TASK-0228). Read by `readLine`,
   * and converted once more when the confirm or the autosave is built.
   */
  qtyText: string;
  /** Raw text of the Ordered box. */
  orderedQtyText: string;
  /** Per-each when `caseMode` is off, per-case when on. */
  unitCostCents: number;
  unitsPerCase: number | null;
  caseMode: boolean;
  unit: 'ea' | 'lb';
  productUnit: 'ea' | 'lb' | null;
  productId: number | null;
  productName: string;
  confidence: MatchConfidence;
  matchMethod: string;
  candidates: MatchCandidate[];
  vendorSku: string | null;
  currentPriceCents: number | null;
  systemUnitCostCents: number | null;
  newPriceCents: number | null;
  warnings: ParserWarning[];
  warningsAcked: boolean;
  shippedCaseCount: number | null;
  shippedWeightLbs: number | null;
  /**
   * TASK-0243 (FR-LEARN): what a hand match teaches the vendor link (see `handMatchEnrichment`).
   * `vendorSku` above is the item number the link holds for the line's CURRENT product.
   */
  printedSku: string | null;
  printedPack: number | null;
  /**
   * TASK-0243 review R1-7: the pack this vendor's link holds for the line's CURRENT product (null:
   * no link, no pack, or not known after a reload of a picked line). Display only: shown when a hand
   * match will replace it with a different printed pack. Not saved in the draft.
   */
  linkPack: number | null;
  /** Another product of this vendor holds `printedSku`; its name is shown, the number left off. */
  skuOwnerId: number | null;
  skuOwnerName: string | null;
  skuChoice: 'keep' | 'use';
  /** TASK-0243: the printed barcode or item number names this inactive product (never matched). */
  inactiveMatch: InactiveMatch | null;
  /**
   * TASK-0191: the operator's "confirm this now" toggle. Only meaningful
   * when `productId !== null` — an unmatched line can never be confirmed,
   * so it is always treated as excluded regardless of this flag.
   * TASK-0242 (FR-IMPORT-B): starts true only for a decided match (barcode,
   * vendor item number, PO line, or a person's pick: confidence `high`); a
   * guess starts unticked until the reviewer ticks it. Kept in the autosave
   * draft, so a reload keeps the reviewer's ticks.
   */
  included: boolean;
};

/**
 * The unit a line's quantity boxes take: up to 3 decimals on a weighed line,
 * whole cases in case mode, whole eaches otherwise. Whole cases match every
 * case-mode line imported so far (0 fractional of 9,272, read-only prod
 * check 2026-10-13); an odd half case is entered with case mode off.
 */
function boxUnit(line: Pick<ReviewLine, 'unit' | 'caseMode'>): QuantityUnit {
  if (line.unit === 'lb') return 'lb';
  return line.caseMode ? 'pack' : 'ea';
}

/** A review line with both quantity boxes read; the shape the payload helpers take. */
type ReadLine = ReviewLine & { qty: number; orderedQty: number };

/** Both boxes read, or null while either is blank or unreadable. */
function readLine(line: ReviewLine): ReadLine | null {
  const unit = boxUnit(line);
  const received = checkQuantity(line.qtyText, unit).quantity;
  const ordered = checkQuantity(line.orderedQtyText, unit).quantity;
  if (received === null || ordered === null) return null;
  return { ...line, qty: received, orderedQty: ordered };
}

/**
 * TASK-0242: the invoice says nothing shipped but something was ordered (out of stock). Such a
 * line never blocks Confirm: it goes on the order as qty 0 with what was ordered.
 */
function isOutOfStockLine(line: ReviewLine): boolean {
  const read = readLine(line);
  return read !== null && read.qty === 0 && read.orderedQty > 0;
}

/** Why a line's boxes do not read, one sentence per box; empty when both read. */
function lineQuantityProblems(line: ReviewLine): string[] {
  const unit = boxUnit(line);
  const name = line.productName || line.description || `Line ${line.key + 1}`;
  const received = checkQuantity(line.qtyText, unit).problem;
  const ordered = checkQuantity(line.orderedQtyText, unit).problem;
  return [
    ...(received !== null
      ? [`${name}: ${line.caseMode ? 'cases in' : 'received'} ${received}.`]
      : []),
    ...(ordered !== null ? [`${name}: ordered ${ordered}.`] : []),
  ];
}

/**
 * TASK-0243 (FR-LEARN): under a hand-matched line, what confirming will save on this vendor's
 * link (the printed item number and pack), and the "keep / use invoice" question when the link
 * already holds a different item number. Nothing shows when nothing would be saved.
 */
function LearnNote({
  line,
  onChoose,
}: {
  line: ReviewLine;
  onChoose: (choice: 'keep' | 'use') => void;
}) {
  if (line.productId === null || line.matchMethod !== 'manual') return null;
  const apply = handMatchEnrichment(line);
  const conflict = itemNumberConflict(line);
  const heldByAnother =
    line.printedSku !== null &&
    line.skuOwnerId !== null &&
    line.skuOwnerId !== line.productId;
  const replacesPack =
    apply?.caseQtyResolution === 'set_vendor_override' &&
    line.printedPack != null &&
    line.linkPack != null &&
    line.linkPack !== line.printedPack;
  const saves = [
    ...(apply?.vendorSku && line.printedSku ? [`item number ${line.printedSku}`] : []),
    ...(apply?.caseQtyResolution === 'set_vendor_override' && line.printedPack
      ? [`${num(line.printedPack)} per case`]
      : []),
  ];
  if (saves.length === 0 && !conflict && !heldByAnother) return null;
  const option = (choice: 'keep' | 'use', label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={line.skuChoice === choice}
      onClick={() => onChoose(choice)}
      className={cn(
        'text-2xs rounded-xs border px-1.5 py-0.5 whitespace-nowrap transition-colors duration-100',
        line.skuChoice === choice
          ? 'border-signal bg-signal text-ink-inverse'
          : 'border-line-strong bg-surface-sunken text-ink hover:border-ink-faint'
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="text-2xs text-ink-muted mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
      {conflict && !heldByAnother ? (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-ink">
            Item number on file <Code>{conflict.link}</Code> · invoice says{' '}
            <Code>{conflict.invoice}</Code>:
          </span>
          <span
            role="radiogroup"
            aria-label={`Item number for ${line.productName}`}
            className="flex items-center gap-1"
          >
            {option('keep', 'Keep')}
            {option('use', 'Use invoice')}
          </span>
        </span>
      ) : null}
      {heldByAnother ? (
        <span>
          Item number <Code>{line.printedSku}</Code> is on{' '}
          {line.skuOwnerName ?? 'another product'} for this vendor, so it is not saved
          here.
        </span>
      ) : null}
      {replacesPack ? (
        <span className="text-ink">
          Pack on file {num(line.linkPack!)} · invoice says {num(line.printedPack!)}.
        </span>
      ) : null}
      {saves.length > 0 ? (
        <span>On confirm, saves {saves.join(' and ')} for this vendor.</span>
      ) : null}
    </div>
  );
}

/**
 * TASK-0243 (FR-LEARN): the product of this vendor that already holds the line's printed item number,
 * as the matcher found it: the line's own match by item number, else (review R1-5) the inactive
 * product its item number named (that product's link holds the number, so a replacement product
 * cannot be given it; the reviewer is told before confirming, not by a 409 after).
 */
export function skuOwnerOf(
  line: Pick<MatchedLine, 'matchMethod' | 'matchedProductId' | 'matchedProductName'> & {
    inactiveMatch?: InactiveMatch | null;
  }
): { skuOwnerId: number | null; skuOwnerName: string | null } {
  if (line.matchMethod === 'vendor_sku' && line.matchedProductId != null) {
    return {
      skuOwnerId: line.matchedProductId,
      skuOwnerName: line.matchedProductName ?? null,
    };
  }
  const inactive = line.inactiveMatch ?? null;
  if (inactive && inactive.method === 'vendor_sku') {
    return { skuOwnerId: inactive.productId, skuOwnerName: inactive.productName ?? null };
  }
  return { skuOwnerId: null, skuOwnerName: null };
}

function buildLines(record: InvoiceImportDetail): ReviewLine[] {
  const matched = record.matched ?? record.matchedJson ?? [];
  const base = matched.map((line, index): ReviewLine => {
    const uom = (line.extractedLine?.uom ?? '').toUpperCase();
    const unitsPerCase = line.extractedLine?.unitsPerCase ?? null;
    const casePrice = line.extractedLine?.casePrice ?? null;
    const perEach = dollarsToCents(line.extractedLine?.unitPrice ?? 0);
    const caseMode = /^(CS|CASE|CSE|CA|CTN)$/i.test(uom) && (unitsPerCase ?? 0) > 1;
    // Landing in case mode, prefer the case price actually printed on the
    // invoice over multiplying a divided-down per-each figure back up.
    const cost =
      caseMode && unitsPerCase
        ? casePrice != null
          ? dollarsToCents(casePrice)
          : perEach * unitsPerCase
        : perEach;
    const received = line.extractedLine?.qty ?? 0;
    const ordered = line.extractedLine?.orderedQty ?? received;
    const parsedUnit = line.extractedLine?.unit;
    return {
      key: index,
      description: line.extractedLine?.description ?? '',
      sku: line.extractedLine?.sku ?? '',
      upc: line.extractedLine?.upc ?? '',
      qtyText: quantityText(received),
      orderedQtyText: quantityText(ordered),
      unitCostCents: cost,
      unitsPerCase,
      caseMode,
      // 'unknown' is the parser saying it could not classify — treat it as the
      // product's own unit when we know it, so the mismatch guard does not
      // fire on a line the operator has not been asked about yet.
      unit:
        parsedUnit === 'lb' || parsedUnit === 'ea'
          ? parsedUnit
          : (line.productUnit ?? 'ea'),
      productUnit: line.productUnit ?? null,
      productId: line.matchedProductId,
      productName: line.matchedProductName ?? '',
      confidence: line.confidence ?? 'none',
      matchMethod: line.matchMethod ?? 'none',
      candidates: line.candidates ?? [],
      vendorSku: line.vendorSku ?? null,
      currentPriceCents: line.currentPriceCents ?? null,
      systemUnitCostCents: line.systemUnitCostCents ?? null,
      newPriceCents: null,
      warnings: line.extractedLine?.warnings ?? [],
      warningsAcked: false,
      shippedCaseCount: line.extractedLine?.shippedCaseCount ?? null,
      shippedWeightLbs:
        line.extractedLine?.shippedWeightLbs != null
          ? Number(line.extractedLine.shippedWeightLbs)
          : null,
      // TASK-0243 (FR-LEARN): the item number and pack the invoice printed, and who holds the
      // number already (the parser matched it by item number to that product).
      printedSku: printedVendorSku(line.extractedLine),
      printedPack: unitsPerCase,
      linkPack: line.vendorItemUnitsPerCase ?? null,
      ...skuOwnerOf(line),
      skuChoice: 'keep',
      inactiveMatch: line.inactiveMatch ?? null,
      // TASK-0242: only a decided match starts ticked; a guess waits for the reviewer.
      included: line.matchedProductId != null && (line.confidence ?? 'none') === 'high',
    };
  });

  // Re-apply a saved draft on top, keyed by line index. `rematch` clears the
  // draft server-side precisely because those keys would no longer line up.
  const draft = record.reviewDraftJson;
  if (!draft || !Array.isArray(draft.lines)) return base;
  const byKey = new Map(draft.lines.map((line) => [line.key, line]));
  return base.map((line) => {
    const saved = byKey.get(line.key);
    if (!saved) return line;
    // TASK-0242: how the product was chosen and the tick come back from the draft. A draft saved
    // without them (before this change, or by the Svelte admin) falls back: a product other than
    // the parser's was picked by a person; the parser's own guess stays a guess, unticked.
    const pickedByHand = saved.productId != null && saved.productId !== line.productId;
    const confidence = saved.confidence ?? (pickedByHand ? 'high' : line.confidence);
    const matchMethod = saved.matchMethod ?? (pickedByHand ? 'manual' : line.matchMethod);
    return {
      ...line,
      productId: saved.productId,
      productName: saved.productName || line.productName,
      // The product's own fields as the screen last had them (a picked product's, not the
      // parser's first guess's).
      productUnit: saved.productUnit !== undefined ? saved.productUnit : line.productUnit,
      currentPriceCents:
        saved.currentPriceCents !== undefined
          ? saved.currentPriceCents
          : line.currentPriceCents,
      systemUnitCostCents:
        saved.systemUnitCostCents !== undefined
          ? saved.systemUnitCostCents
          : line.systemUnitCostCents,
      confidence,
      matchMethod,
      // TASK-0243: the link's item number for the saved product, and the "keep / use invoice"
      // answer (kept as the saved choice's replaceVendorSku). A draft saved without it (before
      // FR-LEARN) knows the matcher's product's link only: for another product it is not known
      // (review R1-6), so no "on file" number is shown; the server reads the link itself.
      vendorSku:
        saved.vendorSku !== undefined
          ? saved.vendorSku
          : saved.productId === line.productId
            ? line.vendorSku
            : null,
      linkPack: saved.productId === line.productId ? line.linkPack : null,
      skuChoice: saved.enrichmentApply?.replaceVendorSku === true ? 'use' : 'keep',
      qtyText: quantityText(saved.qty),
      orderedQtyText: quantityText(saved.orderedQty),
      unitCostCents: saved.unitCostCents,
      unit: saved.unit,
      caseMode: saved.caseMode,
      included: saved.productId != null && (saved.included ?? confidence === 'high'),
      newPriceCents: saved.newPriceCents,
      warningsAcked: saved.warningsAcked,
    };
  });
}

/**
 * TASK-0230 (owner rule C45): the delivery date to prefill, in the API's own order: the ship
 * date, then the printed delivery date, then the printed invoice date. A printed date after
 * today is unreadable and skipped. Blank when nothing usable was printed.
 */
export function prefillDeliveredOn(
  record: Pick<InvoiceImportDetail, 'shipDate' | 'extractedJson'>,
  today: string
): string {
  for (const candidate of [
    record.shipDate,
    record.extractedJson?.deliveryDate,
    record.extractedJson?.invoiceDate,
  ]) {
    if (
      typeof candidate === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(candidate) &&
      candidate <= today
    ) {
      return candidate;
    }
  }
  return '';
}

/**
 * TASK-0250 (FR-TODAY): what the Today link narrowed the inbox to, in words, with a way back to every
 * import. "From Oct 4 on · held-back lines only · Show all".
 */
export function InboxWindowNote({
  since,
  kind,
  onClear,
}: {
  since: string | undefined;
  kind: ImportKindFilter | undefined;
  onClear: () => void;
}) {
  const parts = [
    since ? `From ${dayLabel(since)} on` : null,
    kind === 'followup'
      ? 'held-back lines only'
      : kind === 'original'
        ? 'held-back lines left out'
        : null,
  ].filter((part): part is string => part !== null);
  return (
    <div
      className="text-2xs text-ink-muted mt-2 flex flex-wrap items-center gap-x-1.5 px-1"
      data-testid="inbox-window-note"
    >
      <span>{parts.join(' · ')}</span>
      <span className="text-ink-faint">·</span>
      <button type="button" onClick={onClear} className="text-signal-ink hover:underline">
        Show all
      </button>
    </div>
  );
}

/** Why Re-parse and Re-match are off on a follow-up import. Same sentence as the API's refusal. */
const HELD_BACK_HINT =
  'This is the held-back part of an invoice; change the original instead.';

/**
 * Whether the invoice file pane is open: the person's last choice, else open on a screen wide
 * enough to hold it beside the review (the review keeps its room below that).
 */
const SHOW_FILE_KEY = 'kk.import.showInvoiceFile';
function readShowFile(): boolean {
  try {
    const stored = localStorage.getItem(SHOW_FILE_KEY);
    if (stored === 'yes') return true;
    if (stored === 'no') return false;
  } catch {
    // Blocked storage: fall through to the screen-width default.
  }
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 1440px)').matches
    : true;
}
function storeShowFile(show: boolean) {
  try {
    localStorage.setItem(SHOW_FILE_KEY, show ? 'yes' : 'no');
  } catch {
    // Private window or blocked storage: the choice just lasts until the page is left.
  }
}

// Exported (only) so its own test file can mount it directly, without
// dragging in the inbox/upload panel or router search plumbing that
// `InvoiceImportScreen` owns — `ReviewPane` takes `importId` as a plain
// prop and needs neither.
export function ReviewPane({
  importId,
  onDismissed,
  inboxRow = null,
}: {
  importId: number;
  /** The import was just dismissed; the screen decides whether to leave this pane. */
  onDismissed?: () => void;
  /**
   * TASK-0244: this import's row in the inbox (polled while the review is open), for "Dina opened it
   * after you". Null when the inbox does not list it.
   */
  inboxRow?: InvoiceImportRow | null;
}) {
  const queryClient = useQueryClient();
  const me = useSession()?.id ?? null;
  const navigate = useNavigate();
  const vendors = useVendorOptions();
  const restore = useRestoreImport(importId);
  const [dismissOpen, setDismissOpen] = useState(false);

  const record = useQuery({
    queryKey: importKeys.detail(importId),
    queryFn: () => api.get<InvoiceImportDetail>(`/ai/invoice-imports/${importId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'parsing' ? 3000 : false;
    },
  });

  const [lines, setLines] = useState<ReviewLine[]>([]);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  /** TASK-0244: the reviewer says the paper has no invoice number (nothing is sent, as before). */
  const [noNumber, setNoNumber] = useState(false);
  const [notes, setNotes] = useState('');
  const [asDraft, setAsDraft] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [staleConflict, setStaleConflict] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // TASK-0191: which unmatched line is getting the single inline "Create
  // product" dialog (null = closed), and which unmatched lines are
  // checked for the bulk-create table.
  const [createLineKey, setCreateLineKey] = useState<number | null>(null);
  const [bulkKeys, setBulkKeys] = useState<Set<number>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  /**
   * TASK-0242: lines whose picked suggestion's product details are still loading, by line key,
   * with that pick's token (see `pickSuggestion`). Confirm and the autosave wait for them.
   */
  const [refreshing, setRefreshing] = useState<Record<number, number>>({});
  const loadedFor = useRef<string | null>(null);
  // TASK-0242 review R1-1: the newest pick per line key, and a counter to tell picks apart. A
  // product read answers only the pick that asked for it, on the lines it was asked from.
  const pickTokens = useRef(new Map<number, number>());
  // TASK-0242 review R2-1: the line as it was before the first of the picks still pending on it.
  // Overlapping picks (a double click, two chips) share it, so a failed read puts back the line
  // from before any of them, never an earlier pick's half-made line.
  const prePick = useRef(new Map<number, ReviewLine>());
  const nextPickToken = useRef(0);
  // The vendor the product read's cost is taken for: the one chosen when the read answers.
  const vendorIdRef = useRef<number | null>(null);
  useEffect(() => {
    vendorIdRef.current = vendorId;
  }, [vendorId]);
  // TASK-0230 (owner rule C45): the day the goods arrived, and the add-to-stock answers.
  const today = localToday();
  const [deliveredOn, setDeliveredOn] = useState('');
  const [deliveryDateDemanded, setDeliveryDateDemanded] = useState(false);
  /**
   * Add-to-stock answers by `countAnswerKey` (review line + the question shown: product,
   * delivery day, count); unticked (absent) is the default for a line delivered before the
   * count's day. Re-matching a line to another product therefore never carries an answer over
   * (TASK-0230 review F1); a re-match also drops the line's answers outright.
   */
  const [stockTicks, setStockTicks] = useState<Record<string, boolean>>({});
  const [serverQuestions, setServerQuestions] = useState<{
    deliveredOn: string;
    byKey: Map<number, CountQuestion>;
  } | null>(null);

  // TASK-0236 (INC-8): "which PO?" — the vendor's open or checked-in-not-billed orders.
  /** "A different delivery" for these orders (sent as acknowledgeOpenOrderIds). */
  const [differentFrom, setDifferentFrom] = useState<number[] | null>(null);
  const [pickedOrder, setPickedOrder] = useState<number | null>(null);
  /** Orders a 409 OPEN_ORDER_EXISTS named (when the step had not shown them yet). */
  const [refusedOrders, setRefusedOrders] = useState<{
    orders: OpenOrderChoice[];
    preselectPoId: number | null;
  } | null>(null);
  /** The invoice is already recorded on a PO, or aimed at one (confirm refused). */
  const [recordedRefusal, setRecordedRefusal] = useState<{
    code: 'ALREADY_RECORDED_ON_ORDER' | 'USE_ORDER';
    poId: number;
    message: string;
  } | null>(null);
  /** The vendor reuses invoice numbers: key this one by its date (offered only after the refusal). */
  const [keyByDate, setKeyByDate] = useState(false);

  // TASK-0232: asks before Re-parse (it costs AI and clears the review).
  const [reparseOpen, setReparseOpen] = useState(false);
  const [showFile, setShowFile] = useState(readShowFile);
  const changeShowFile = (show: boolean) => {
    setShowFile(show);
    storeShowFile(show);
  };

  /* TASK-0244 (FR-NEEDS): "Needs you" / "All", the lines kept on "Needs you" once a person has dealt
     with them (so a line does not vanish under the cursor), the j / k line, and this person's marker. */
  const [view, setView] = useState<'needs' | 'all'>('all');
  const [keptKeys, setKeptKeys] = useState<Set<number>>(() => new Set());
  const [cursorKey, setCursorKey] = useState<number | null>(null);
  const lineRefs = useRef(new Map<number, HTMLDivElement>());
  useShortcutHint([{ keys: 'j / k', label: 'next / previous line' }]);

  const data = record.data;
  // A follow-up import carries only the held-back lines: Re-parse and Re-match would replace them
  // with the whole original invoice (or with nothing), so both are off here and refused by the API.
  const heldBackFrom = data?.followUpOfImportId ?? null;
  const invoiceNumberTooLong = invoiceNumber.trim().length > INVOICE_NUMBER_MAX_LENGTH;
  // The warning is only for a review that can still be confirmed.
  const dup = useDuplicateInvoiceCheck({
    vendorId,
    invoiceNumber,
    enabled: data?.status === 'matched' && !data.confirmed,
  });
  const dupVendorName = vendors.data?.find((v) => v.id === vendorId)?.name ?? null;
  const storedVendorName =
    vendors.data?.find((v) => v.id === data?.vendorId)?.name ?? null;
  const aimedAt = data?.targetPurchaseOrderId ?? null;
  const reviewable = data?.status === 'matched' && !data.confirmed;
  const candidatesQuery = useOrderCandidates({
    vendorId: data?.vendorId ?? null,
    importId,
    enabled: reviewable && aimedAt === null && data?.vendorId != null,
  });
  const candidates: OpenOrderChoice[] =
    refusedOrders?.orders ?? candidatesQuery.data?.candidates ?? [];
  const preselect =
    refusedOrders?.preselectPoId ?? candidatesQuery.data?.preselectPoId ?? null;
  const selectedOrder =
    pickedOrder !== null && candidates.some((c) => c.poId === pickedOrder)
      ? pickedOrder
      : preselect;
  const differentChosen =
    differentFrom !== null && candidates.every((c) => differentFrom.includes(c.poId));
  const whichOrderOpen = aimedAt === null && candidates.length > 0 && !differentChosen;

  const aim = useMutation({
    mutationFn: (purchaseOrderId: number | null) =>
      api.put<{ id: number; targetPurchaseOrderId: number | null }>(
        `/ai/invoice-imports/${importId}/target`,
        { purchaseOrderId, expectedUpdatedAt: data?.updatedAt }
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      if (result.targetPurchaseOrderId !== null) {
        void navigate({
          to: '/purchasing/$id/invoice',
          params: { id: String(result.targetPurchaseOrderId) },
          search: { importId, mode: 'scan' },
        });
      } else {
        setDifferentFrom(null);
        setRefusedOrders(null);
        toast.success('This invoice is no longer aimed at a PO.');
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // Hydrate once per (import, parse version) so an autosave or a poll does not
  // stomp on what the operator is typing.
  useEffect(() => {
    if (!data) return;
    const stamp = `${data.id}:${data.updatedAt}`;
    if (loadedFor.current === stamp) return;
    loadedFor.current = stamp;
    setLines(buildLines(data));
    setVendorId(data.reviewDraftJson?.vendorId ?? data.vendorId ?? null);
    setInvoiceNumber(
      data.reviewDraftJson?.invoiceNumber ?? data.extractedJson?.invoiceNumber ?? ''
    );
    setNotes(data.reviewDraftJson?.notes ?? '');
    setNoNumber(false);
    // TASK-0244: open on "Needs you" when clean lines may be folded; every line otherwise.
    setView(
      foldingFor({
        vendorConfirmedImports: data.vendorConfirmedImports,
        today: localToday(),
      }).fold
        ? 'needs'
        : 'all'
    );
    setKeptKeys(new Set());
    setCursorKey(null);
    setAsDraft(data.reviewDraftJson?.draft ?? false);
    setDeliveredOn(prefillDeliveredOn(data, localToday()));
    setDeliveryDateDemanded(false);
    setServerQuestions(null);
    // A new parse or match: its lines are not the ones the operator answered for.
    setStockTicks({});
    setBulkKeys(new Set());
    // Lines rebuilt: a product read still in flight was asked for the old ones (R1-1).
    pickTokens.current.clear();
    prePick.current.clear();
    setRefreshing({});
    // TASK-0236: a new parse or another import starts its "which PO?" answer over.
    setDifferentFrom(null);
    setPickedOrder(null);
    setRefusedOrders(null);
    setRecordedRefusal(null);
    setKeyByDate(false);
    setDirty(false);
    setStaleConflict(null);
  }, [data]);

  // TASK-0244: "being reviewed by". Opening a review that can still be confirmed stamps this person on
  // the import (once per pane); the answer names whoever had it open before. Display only: a failure
  // changes nothing on the review. The PO's invoice screen stamps the same way (review R1-4).
  const myMarker = useReviewingMarker(importId, reviewableImport(data));

  const patch = (key: number, next: Partial<ReviewLine>) => {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...next } : line))
    );
    // TASK-0230 review F1: a line matched to another product (picked, suggested, created or
    // skipped) starts its stock question over; an answer belongs to the product it was given for.
    const before = lines.find((line) => line.key === key);
    if ('productId' in next && before && before.productId !== next.productId) {
      const prefix = `${key}|`;
      setStockTicks((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(prefix)))
      );
    }
    // TASK-0242: a line matched another way (picked, suggested or created) leaves the bulk
    // "Create products" selection, so that table never makes a product for a matched line.
    if (next.productId != null) {
      setBulkKeys((prev) => {
        if (!prev.has(key)) return prev;
        const rest = new Set(prev);
        rest.delete(key);
        return rest;
      });
    }
    setDirty(true);
  };

  /**
   * TASK-0242: a person picked one of the line's suggestions. The line takes that product's
   * unit, retail price and system cost (not the first guess's), read from the product itself;
   * Confirm waits until they are in. A pick is a person's decision, so it is recorded as one
   * (high, manual) and ticked; a retail price typed for the previous product is cleared.
   */
  const pickSuggestion = (key: number, candidate: MatchCandidate) => {
    const productId = candidate.productId;
    if (!pickTokens.current.has(key)) {
      const original = lines.find((line) => line.key === key);
      if (original) prePick.current.set(key, original);
    }
    const before = prePick.current.get(key);
    patch(key, {
      productId,
      productName: candidate.productName,
      productUnit: null,
      currentPriceCents: null,
      systemUnitCostCents: null,
      newPriceCents: null,
      confidence: 'high',
      matchMethod: 'manual',
      included: true,
      // TASK-0243: the link's item number and the answer belong to the product they were for.
      vendorSku: null,
      linkPack: null,
      skuChoice: 'keep',
    });
    // TASK-0242 review R1-1: this pick's stamp. The answer is applied only while it is still the
    // newest pick on this line, the lines are the ones it was made on (no re-match or re-parse
    // since) and the line still holds this product. Otherwise it is ignored, success and failure.
    const token = ++nextPickToken.current;
    const stamp = loadedFor.current;
    pickTokens.current.set(key, token);
    setRefreshing((prev) => ({ ...prev, [key]: token }));
    const current = () =>
      pickTokens.current.get(key) === token && loadedFor.current === stamp;
    const stillPicked = (line: ReviewLine) =>
      line.key === key && line.productId === productId;
    api
      .get<ProductDetail & { unit?: string | null }>(`/products/${productId}`, {
        params: { days: 1 },
      })
      .then((detail) => {
        if (!current()) return;
        setLines((prev) =>
          prev.map((line) =>
            stillPicked(line)
              ? { ...line, ...productFieldsForLine(detail, vendorIdRef.current) }
              : line
          )
        );
        setDirty(true);
      })
      .catch((error: unknown) => {
        if (!current()) return;
        // TASK-0242 review R1-2: a pick whose unit and costs never came is not left half-made
        // (no unit check, no cost or retail comparison): the line goes back to what it was.
        setLines((prev) =>
          prev.map((line) =>
            stillPicked(line) && before
              ? {
                  ...line,
                  productId: before.productId,
                  productName: before.productName,
                  productUnit: before.productUnit,
                  currentPriceCents: before.currentPriceCents,
                  systemUnitCostCents: before.systemUnitCostCents,
                  newPriceCents: before.newPriceCents,
                  confidence: before.confidence,
                  matchMethod: before.matchMethod,
                  included: before.included,
                  vendorSku: before.vendorSku,
                  linkPack: before.linkPack,
                  skuChoice: before.skuChoice,
                }
              : line
          )
        );
        toast.error(
          `Could not load ${candidate.productName}: ${errorMessage(error)} The line was put back as it was; pick it again.`
        );
      })
      .finally(() => {
        if (pickTokens.current.get(key) === token) {
          pickTokens.current.delete(key);
          prePick.current.delete(key);
        }
        setRefreshing((prev) => {
          if (prev[key] !== token) return prev;
          const rest = { ...prev };
          delete rest[key];
          return rest;
        });
      });
  };

  // TASK-0191: a product created inline or in bulk auto-attaches to the
  // line that triggered it — same effect as picking it from the
  // `ProductPicker`, no second search step.
  const attachCreatedProduct = (key: number, product: CreatedProduct) => {
    patch(key, {
      productId: product.id,
      productName: product.name,
      productUnit: product.unit,
      currentPriceCents: product.price,
      systemUnitCostCents: product.cost ?? null,
      confidence: 'high',
      matchMethod: 'manual',
      included: true,
      newPriceCents: null,
      // TASK-0243: a new product's link was made with the printed item number, if any.
      vendorSku: null,
      linkPack: null,
      skuChoice: 'keep',
    });
  };

  /* Draft autosave — optimistic concurrency on `expectedUpdatedAt`. */
  const saveDraft = useMutation({
    mutationFn: (draft: SavedReviewDraft & { expectedUpdatedAt: string }) =>
      api.put<{ id: number; updatedAt: string }>(
        `/ai/invoice-imports/${importId}/draft`,
        draft
      ),
    onSuccess: () => setDirty(false),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        // Someone else re-parsed or re-matched this import. Overwriting would
        // silently re-attach edits to different lines, so autosave stops here.
        setStaleConflict(error.message);
      }
    },
  });

  const draftPayload = useCallback(():
    | (SavedReviewDraft & { expectedUpdatedAt: string })
    | null => {
    if (!data) return null;
    // The draft stores numbers. While any box is blank or unreadable the
    // autosave waits (the last saved draft stands) rather than store a 0
    // the operator never typed.
    const read = lines.map(readLine);
    if (read.some((line) => line === null)) return null;
    const savedLines: SavedReviewLine[] = (read as ReadLine[]).map((line) => ({
      key: line.key,
      productId: line.productId,
      productName: line.productName.slice(0, 512),
      qty: line.qty,
      orderedQty: line.orderedQty,
      unitCostCents: Math.max(0, Math.round(line.unitCostCents)),
      unit: line.unit,
      productUnit: line.productUnit,
      caseMode: line.caseMode,
      currentPriceCents: line.currentPriceCents,
      systemUnitCostCents: line.systemUnitCostCents,
      newPriceCents: line.newPriceCents,
      warningsAcked: line.warningsAcked,
      // TASK-0243: what a hand match will save (and the "use invoice" answer) survives a reload.
      enrichmentApply: handMatchEnrichment(line),
      // TASK-0242: the tick and how the product was chosen survive a reload.
      included: line.included,
      confidence: line.confidence,
      matchMethod: line.matchMethod.slice(0, 64),
      vendorSku: line.vendorSku,
    }));
    return {
      vendorId,
      invoiceNumber: invoiceNumber.slice(0, 128),
      notes: notes.slice(0, 2048),
      draft: asDraft,
      header: {},
      lines: savedLines,
      expectedUpdatedAt: data.updatedAt,
    };
  }, [data, lines, vendorId, invoiceNumber, notes, asDraft]);

  useEffect(() => {
    if (!dirty || staleConflict || !data || data.status !== 'matched') return;
    // TASK-0242 review R1-2: not while a picked suggestion's unit and costs are loading, so a
    // draft never stores a pick without them; the save follows once they are in.
    if (Object.keys(refreshing).length > 0) return;
    const timer = setTimeout(() => {
      const payload = draftPayload();
      if (payload) saveDraft.mutate(payload);
    }, 1500);
    return () => clearTimeout(timer);
    // `saveDraft` is a stable mutation object; excluding it keeps the timer
    // from restarting on every in-flight state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, staleConflict, data, draftPayload, refreshing]);

  const retry = useMutation({
    mutationFn: (forcePdfFileMode: boolean) =>
      api.post(`/ai/invoice-imports/${importId}/retry`, { forcePdfFileMode }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      toast.success('Re-queued for parsing.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rematch = useMutation({
    mutationFn: (nextVendorId: number) =>
      api.post(`/ai/rematch-invoice/${importId}`, { vendorId: nextVendorId }),
    onSuccess: () => {
      loadedFor.current = null;
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      toast.success('Re-matched against the new vendor. Any saved draft was cleared.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  /* ── Derived review state ─────────────────────────────────────────── */

  const assigned = lines.filter((line) => line.productId !== null);
  const unmatched = lines.filter((line) => line.productId === null);
  // TASK-0191: what's actually going onto the PO right now — a matched
  // line the operator has NOT unchecked. On the "nothing excluded" happy
  // path (the only state before this task existed) this is exactly
  // `assigned`, so the guards below fire identically to before.
  const confirmedNow = assigned.filter((line) => line.included);
  // Everything NOT going onto the PO this time: matched-but-unchecked
  // lines, plus every unmatched line. TASK-0191's whole point is that
  // this set is never silently dropped — see `remainderLines` below.
  const deferredNow = lines.filter((line) => !confirmedNow.includes(line));
  // TASK-0242: guesses the reviewer has not ticked, and lines that shipped nothing.
  const uncheckedGuesses = assigned.filter(
    (line) => isGuessedMatch(line) && !line.included
  );
  const outOfStockNow = confirmedNow.filter(isOutOfStockLine);
  const refreshingCount = Object.keys(refreshing).length;
  const unitMismatch = confirmedNow.filter(
    (line) => line.productUnit !== null && line.productUnit !== line.unit
  );
  // Every line's boxes must read, held-back and unmatched ones too: they
  // travel in the confirm as remainder lines with their quantities.
  const unreadable = lines.flatMap(lineQuantityProblems);
  const readConfirmed = confirmedNow.flatMap((line) => {
    const read = readLine(line);
    return read ? [read] : [];
  });
  const badQty = readConfirmed.filter((line) => {
    const payload = submitPayloadForLine(line);
    if (payload.qty <= 0 && payload.orderedQty <= 0) return true;
    if (line.unit === 'ea') {
      return !Number.isInteger(payload.qty) || !Number.isInteger(payload.orderedQty);
    }
    return false;
  });
  const quantityProblems = [
    ...unreadable,
    ...badQty.map((line) => {
      const name = line.productName || line.description;
      const payload = submitPayloadForLine(line);
      return payload.qty <= 0 && payload.orderedQty <= 0
        ? `${name}: ordered and received are both zero.`
        : `${name}: each-priced lines need whole eaches.`;
    }),
  ];
  const priceUpdates = confirmedNow.filter(
    (line) => line.newPriceCents !== null && line.newPriceCents !== line.currentPriceCents
  );

  /* TASK-0230 (owner rule C45). A typed date must be in the last 60 days; an older printed date
     is not sent (the API reads it from the invoice itself). With no date, the check asks about
     the earliest possible day, which names every counted item: then the date is required. */
  const deliveredOnShape = /^\d{4}-\d{2}-\d{2}$/.test(deliveredOn);
  const deliveredOnTypedOk =
    deliveredOnShape &&
    deliveredOn <= today &&
    deliveredOn >= addDays(today, -DELIVERY_DATE_WINDOW_DAYS);
  const printedDate = data ? prefillDeliveredOn(data, today) : '';
  const deliveredOnUsable =
    deliveredOnTypedOk || (deliveredOnShape && deliveredOn === printedDate);
  const receiving = !asDraft && confirmedNow.length > 0;
  const stockingNow = confirmedNow.filter((line) => {
    const read = readLine(line);
    return read !== null && submitPayloadForLine(read).qty > 0;
  });
  const countCheck = useCountCheck(
    receiving
      ? deliveredOnUsable
        ? deliveredOn
        : deliveredOn === ''
          ? '2000-01-01'
          : today
      : today,
    receiving ? stockingNow.map((line) => line.productId!) : [],
    today
  );
  const countedWithoutDate =
    receiving && deliveredOn === '' && countCheck.questions.length > 0;
  const deliveredOnRequired = deliveryDateDemanded || countedWithoutDate;
  const questionByKey = new Map<number, CountQuestion>();
  if (receiving && deliveredOnUsable && deliveredOn < today) {
    const byProduct = new Map(countCheck.questions.map((q) => [q.productId, q]));
    for (const line of stockingNow) {
      const question = byProduct.get(line.productId!);
      if (question)
        questionByKey.set(line.key, { ...question, lineRef: `line:${line.key}` });
    }
    if (serverQuestions?.deliveredOn === deliveredOn) {
      // Only while the line is still matched to the product the server asked about.
      for (const [key, question] of serverQuestions.byKey) {
        if (
          stockingNow.some(
            (line) => line.key === key && line.productId === question.productId
          )
        )
          questionByKey.set(key, question);
      }
    }
  }
  const askedLines = stockingNow.filter((line) => questionByKey.has(line.key));
  /** Where a line's answer is stored: the line and the question it is shown now. */
  const answerKeyOf = (key: number): string | null => {
    const question = questionByKey.get(key);
    return question ? countAnswerKey(String(key), question) : null;
  };
  /** The line's answer; null = unanswered (a same-day line), false for an unasked line. */
  const answerOf = (key: number): boolean | null => {
    const question = questionByKey.get(key);
    if (!question) return false;
    return stockAnswer(
      {
        key: countAnswerKey(String(key), question),
        sameDayAsCount: question.sameDayAsCount,
      },
      stockTicks
    );
  };
  const unanswered = askedLines.filter((line) => answerOf(line.key) === null);
  const leftOutLines = askedLines.filter((line) => answerOf(line.key) === false);

  /* TASK-0244 (FR-NEEDS): why each line needs a person, and which lines the panel shows. Clean lines
     fold into one row on "Needs you" (never for a vendor's first 3 recorded invoices, never in Pesach
     season). A line that needed a person stays listed after they dealt with it, marked checked, until
     the filter is chosen again; so a ticked guess does not vanish under the cursor. Display only. */
  const needsByKey = new Map<number, NeedsReason[]>();
  for (const line of lines) {
    const perEach = derivedPerEachCents(line);
    needsByKey.set(
      line.key,
      lineNeeds({
        productId: line.productId,
        confidence: line.confidence,
        included: line.included,
        productUnit: line.productUnit,
        unit: line.unit,
        systemUnitCostCents: line.systemUnitCostCents,
        invoiceUnitCostCents: perEach != null ? Math.round(perEach) : line.unitCostCents,
        received: checkQuantity(line.qtyText, boxUnit(line)).quantity,
        ordered: checkQuantity(line.orderedQtyText, boxUnit(line)).quantity,
        countQuestion: questionByKey.has(line.key),
        warningCodes: line.warnings.map((warning) => String(warning.code)),
        warningsAcked: line.warningsAcked,
      })
    );
  }
  const needingLines = lines.filter(
    (line) => (needsByKey.get(line.key) ?? []).length > 0
  );
  const folding = foldingFor({
    vendorConfirmedImports: data?.vendorConfirmedImports,
    today,
  });
  const showAll = view === 'all' || !folding.fold;
  const shownLines = showAll
    ? lines
    : lines.filter(
        (line) => (needsByKey.get(line.key) ?? []).length > 0 || keptKeys.has(line.key)
      );
  const foldedCount = lines.length - shownLines.length;
  const needingSig = needingLines.map((line) => line.key).join(',');
  useEffect(() => {
    if (needingSig === '') return;
    const keys = needingSig.split(',').map(Number);
    setKeptKeys((prev) =>
      keys.every((key) => prev.has(key)) ? prev : new Set([...prev, ...keys])
    );
  }, [needingSig]);
  const chooseView = (next: 'needs' | 'all') => {
    // Choosing "Needs you" again starts it over from what needs a person now.
    if (next === 'needs') setKeptKeys(new Set(needingLines.map((line) => line.key)));
    setView(next);
  };

  // TASK-0244: j / k move between the lines on screen, never while typing or in a dialog.
  const shownSig = shownLines.map((line) => line.key).join(',');
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'j' && event.key !== 'k') return;
      if (isTypingOrDialog(event)) return;
      const keys = shownSig === '' ? [] : shownSig.split(',').map(Number);
      if (keys.length === 0) return;
      event.preventDefault();
      setCursorKey((current) => stepLine(keys, current, event.key === 'j' ? 1 : -1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shownSig]);
  useEffect(() => {
    if (cursorKey === null) return;
    const node = lineRefs.current.get(cursorKey);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [cursorKey]);
  /** Why the confirm is held, when it is (the screen can change under an open dialog). */
  const confirmHeldBecause =
    !receiving || asDraft
      ? null
      : countCheck.pending
        ? 'Still checking which items were counted after this delivery.'
        : unansweredMessage(unanswered.length);
  const deliveredOnError =
    deliveredOn !== '' && !deliveredOnUsable
      ? 'Delivered on must be a date in the last 60 days.'
      : deliveredOnRequired && deliveredOn === ''
        ? 'This invoice has items counted on the floor. Enter the day it was delivered.'
        : null;

  // Display only: a line whose boxes do not read adds nothing (and blocks
  // the confirm until it does).
  const confirmedSubtotal = readConfirmed.reduce(
    (sum, line) => sum + caseModeSubtotalCents(line),
    0
  );
  // Lines held back or unmatched are not on the order, so they stay out of the
  // printed-total check; they are only totalled so a gap they explain is named.
  const heldBackSubtotal = useMemo(
    () =>
      lines.reduce((sum, line) => {
        if (line.productId !== null && line.included) return sum;
        const read = readLine(line);
        return read ? sum + caseModeSubtotalCents(read) : sum;
      }, 0),
    [lines]
  );
  const printed = data?.extractedJson;
  const totals = checkInvoiceTotals({
    includedCents: confirmedSubtotal,
    heldBackCents: heldBackSubtotal,
    printedSubtotalCents: printedCents(printed?.subtotal),
    printedTaxCents: printedCents(printed?.tax, true),
    printedTotalCents: printedCents(printed?.total),
  });

  // What the person typed, then (when the invoice number is already on an order) their answer.
  const poNotes = notesWithDuplicateAnswer(notes, dup.noteLine);

  const confirm = useMutation({
    mutationFn: async () => {
      // The one conversion from typed text. Refuse rather than send a blank
      // or unreadable box.
      const read = lines.map(readLine);
      if (read.some((line) => line === null)) {
        throw new Error(
          `${lines.flatMap(lineQuantityProblems).join(' ')} Nothing was sent.`
        );
      }
      const { lineItems, remainderLines } = buildConfirmPayload(read as ReadLine[]);
      // TASK-0230 review F2: refuses, nothing sent, while the count check for the date on
      // screen is still running or an asked line is unanswered. A draft receives nothing.
      const decisions = asDraft
        ? []
        : answersToSend(
            askedLines,
            (line) => answerOf(line.key),
            receiving && countCheck.pending
          );

      const result = await api.post<ConfirmInvoiceResult>(
        `/ai/confirm-invoice/${importId}`,
        {
          lineItems,
          vendorId,
          // TASK-0244 review R1-2: "No number on the paper" sends a blank number, so the API keys the
          // invoice by its date or file, not by a number the parser read.
          ...(noNumber
            ? { invoiceNumber: '' }
            : invoiceNumber.trim()
              ? { invoiceNumber: invoiceNumber.trim() }
              : {}),
          ...(poNotes ? { notes: poNotes } : {}),
          draft: asDraft,
          ...(data?.updatedAt ? { expectedUpdatedAt: data.updatedAt } : {}),
          ...(remainderLines.length > 0 ? { remainderLines } : {}),
          // TASK-0230: a typed (or prefilled) day in the last 60 days; an older printed day is
          // left for the API to read from the invoice.
          ...(!asDraft && deliveredOnTypedOk ? { deliveredOn } : {}),
          // TASK-0236: "a different delivery" from the vendor's open orders; the key-by-date
          // answer for a vendor that reuses invoice numbers.
          ...(!asDraft && differentChosen && differentFrom
            ? { acknowledgeOpenOrderIds: differentFrom }
            : {}),
          ...(keyByDate
            ? { keyOverrideDate: deliveredOnTypedOk ? deliveredOn : today }
            : {}),
          ...(decisions.length > 0
            ? {
                stockDecisions: decisions.map(({ line, addStock }) => ({
                  lineIndex: line.key,
                  addStock,
                })),
              }
            : {}),
        }
      );

      // "Confirm 0 of N" — nothing was confirmed, so there is no PO and
      // nothing to price-update. Short-circuit before touching `po.id`.
      if (result.po === null) {
        return {
          po: null,
          remainderImportId: result.remainderImportId,
          priceFailures: 0,
          priceAttempts: 0,
        };
      }

      // Retail price updates are a SEPARATE, best-effort step. A failure here
      // does not undo the PO, so it must never be reported as a failed import.
      let priceFailures = 0;
      for (const line of priceUpdates) {
        try {
          await api.put(`/products/${line.productId}`, { price: line.newPriceCents });
        } catch {
          priceFailures += 1;
        }
      }
      return {
        po: result,
        remainderImportId: result.remainderImportId,
        priceFailures,
        priceAttempts: priceUpdates.length,
      };
    },
    onSuccess: ({ po, remainderImportId, priceFailures, priceAttempts }) => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      setConfirmOpen(false);
      setConfirmError(null);

      if (po === null) {
        // Nothing confirmed — everything moved to the linked draft import.
        // Navigates straight there, so no toast action is needed here.
        toast.success(
          `Nothing confirmed. All of it was saved to import #${remainderImportId}.`
        );
        void navigate({ to: '/purchasing/import', search: { id: remainderImportId } });
        return;
      }

      queryClient.setQueryData(poKeys.detail(po.id), po);
      invalidateAfterReceive(queryClient);

      const remainderNote =
        remainderImportId != null
          ? ` The rest was saved to import #${remainderImportId}.`
          : '';
      // TASK-0191 round 3 (review fix, finding 5): the more common path —
      // some lines confirmed, some held back — used to only *name* the
      // remainder import's id in toast text and navigate to the new PO
      // instead, leaving the operator to remember the number and find it
      // by eye. An actionable toast reaches it in one click, same as the
      // "confirm 0 of N" path above already does by navigating directly.
      const action = remainderToastAction(
        remainderImportId,
        (id) => void navigate({ to: '/purchasing/import', search: { id } })
      );
      // TASK-0260 (INC-9a): what the invoice's prices did to cost, under the toast.
      const costSummary = costDecisionSummary(po.costDecisions);
      const toastOpts =
        action || costSummary
          ? {
              ...(action ? { action } : {}),
              ...(costSummary ? { description: costSummary } : {}),
            }
          : undefined;
      if (priceFailures > 0) {
        toast.warning(
          `PO #${po.id} created, ${priceFailures} of ${priceAttempts} price updates failed. Fix those on the product pages.${remainderNote}`,
          toastOpts
        );
      } else if (priceAttempts > 0) {
        toast.success(
          `PO #${po.id} created and ${priceAttempts} retail prices updated.${remainderNote}`,
          toastOpts
        );
      } else {
        toast.success(
          `${asDraft ? `Draft PO #${po.id} created.` : `PO #${po.id} created and received.`}${remainderNote}`,
          toastOpts
        );
      }
      void navigate({
        to: '/purchasing/$id',
        params: { id: String(po.id) },
        search: { mode: 'lines' as const },
      });
    },
    onError: (error) => {
      setConfirmOpen(false);
      // TASK-0230: the API asked about counted items, or needs the delivery day first.
      const asked = countQuestionsFromError(error);
      if (asked) {
        const byKey = new Map<number, CountQuestion>();
        for (const question of asked) {
          const key = Number(question.lineRef.replace(/^line:/, ''));
          if (Number.isInteger(key)) byKey.set(key, question);
        }
        setServerQuestions({ deliveredOn, byKey });
      }
      if (refusalCode(error) === DELIVERY_DATE_REQUIRED) setDeliveryDateDemanded(true);
      // TASK-0236: which PO, or already recorded on one.
      const orders = openOrdersFromError(error);
      if (orders) {
        setRefusedOrders(orders);
        setDifferentFrom(null);
        setConfirmError(null);
        return;
      }
      const recorded = recordedOnOrderFromError(error);
      if (recorded) {
        setRecordedRefusal({
          code: 'ALREADY_RECORDED_ON_ORDER',
          poId: recorded.poId,
          message: recorded.message,
        });
        setConfirmError(null);
        return;
      }
      // TASK-0243 (FR-LEARN): the item number is already on another product of this vendor. The
      // line now leaves it off (and says where it is); confirming again records the invoice.
      if (codeOf(error) === 'DUPLICATE_ITEM_NUMBER') {
        const held = detailsOf<{
          productId: number;
          productName: string;
          forProductId: number;
          itemNumber: string;
        }>(error);
        if (held && Number.isInteger(Number(held.productId))) {
          setLines((prev) =>
            prev.map((line) =>
              line.productId === Number(held.forProductId) &&
              line.printedSku === held.itemNumber
                ? {
                    ...line,
                    skuOwnerId: Number(held.productId),
                    skuOwnerName: held.productName ?? null,
                  }
                : line
            )
          );
          setConfirmError(
            `${errorMessage(error)} It is now left off; confirm again to record the invoice.`
          );
          return;
        }
      }
      if (codeOf(error) === 'USE_ORDER') {
        const poId = Number(detailsOf<{ poId: number }>(error)?.poId);
        if (Number.isInteger(poId)) {
          setRecordedRefusal({ code: 'USE_ORDER', poId, message: errorMessage(error) });
          setConfirmError(null);
          return;
        }
      }
      setConfirmError(errorMessage(error));
    },
  });

  /* ── Render ───────────────────────────────────────────────────────── */

  if (record.isPending) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (record.isError || !data) {
    return (
      <div className="p-4">
        <ErrorState
          title="Could not load this import"
          message={errorMessage(record.error)}
          onRetry={() => void record.refetch()}
        />
      </div>
    );
  }

  // TASK-0239: set aside. Nothing to review; Restore brings the lines back.
  if (data.dismissedAt) {
    return (
      <div className="p-4">
        <DismissedPanel
          record={data}
          restoring={restore.isPending}
          onRestore={() => restore.mutate()}
        />
      </div>
    );
  }

  const dismissButton = (
    <Button
      size="xs"
      variant="ghost"
      onClick={() => setDismissOpen(true)}
      title="Set this import aside: a duplicate, not an invoice, or not for the inbox"
    >
      Dismiss
    </Button>
  );
  const dismissDialog = dismissOpen ? (
    <DismissDialog
      importId={importId}
      name={data.sourceFilename ?? `Import #${data.id}`}
      unsaved={dirty}
      onClose={() => setDismissOpen(false)}
      onDismissed={() => {
        setDirty(false);
        onDismissed?.();
      }}
    />
  ) : null;

  if (data.status === 'queued' || data.status === 'parsing') {
    return (
      <div className="p-4">
        {dismissDialog}
        <Panel
          title={data.sourceFilename ?? `Import #${data.id}`}
          actions={dismissButton}
        >
          <div className="flex items-center gap-3 py-6">
            <Loader2 className="text-signal size-5 animate-spin" />
            <div>
              <div className="text-ink text-sm">{importStatusLabel(data.status)}</div>
              <div className="text-2xs text-ink-faint">
                Parsing runs in the background — this page updates itself. Attempt{' '}
                {num(data.parseAttempts)}.
              </div>
              {/* TASK-0244: a busy or silent reading service is tried once more by itself. */}
              {data.status === 'queued' && data.parseError ? (
                <div className="text-2xs text-warn mt-1">{data.parseError}</div>
              ) : null}
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  if (data.status === 'failed') {
    return (
      <div className="space-y-3 p-4">
        {dismissDialog}
        <Panel
          title={data.sourceFilename ?? `Import #${data.id}`}
          actions={dismissButton}
        >
          <div className="border-negative/40 bg-negative-muted rounded border p-3">
            <div className="text-ink text-sm font-medium">Parsing failed</div>
            <p className="text-2xs text-ink-muted mt-1 font-mono leading-relaxed whitespace-pre-wrap">
              {data.parseError ?? 'No detail was recorded.'}
            </p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              icon={<RefreshCw className="size-3.5" />}
              loading={retry.isPending}
              onClick={() => retry.mutate(false)}
            >
              Retry parse
            </Button>
            <Button
              size="sm"
              variant="default"
              loading={retry.isPending}
              onClick={() => retry.mutate(true)}
            >
              Retry as image
            </Button>
          </div>
          <p className="text-2xs text-ink-faint mt-2">
            “Retry as image” skips the cheap text pass and sends the PDF to the model as a
            picture. It costs more but survives scrambled PDF text — the usual cause of a
            failed parse. Make it permanent for this vendor on their Invoice reading tab.
          </p>
        </Panel>
      </div>
    );
  }

  if (data.status === 'confirmed' || data.confirmed) {
    return (
      <div className="p-4">
        <Panel title={data.sourceFilename ?? `Import #${data.id}`}>
          <div className="flex items-center gap-3">
            <Badge tone="positive" dot>
              Imported
            </Badge>
            <span className="text-ink-muted text-xs">
              Confirmed {smartDate(data.updatedAt)}
            </span>
          </div>
          {data.purchaseOrderId ? (
            <Button size="sm" className="mt-3" asChild>
              <Link
                to="/purchasing/$id"
                params={{ id: String(data.purchaseOrderId) }}
                search={{ mode: 'lines' as const }}
              >
                Open PO #{data.purchaseOrderId}
              </Link>
            </Button>
          ) : null}
        </Panel>
      </div>
    );
  }

  const confidence = data.extractionConfidence ?? 'high';
  // TASK-0191: no longer requires `assigned.length > 0` — an operator can
  // now submit "save all N for later" (confirm 0, defer everything) when
  // nothing is ready yet, as long as there is at least one line to act on.
  // TASK-0244: the invoice number is asked every time; "No number on the paper" sends none, as before.
  const invoiceNumberMissing =
    !noNumber && invoiceNumber.trim() === '' && confirmedNow.length > 0;
  const blocked =
    lines.length === 0 ||
    invoiceNumberMissing ||
    unitMismatch.length > 0 ||
    quantityProblems.length > 0 ||
    vendorId === null ||
    (receiving && deliveredOnError !== null) ||
    (receiving && unanswered.length > 0) ||
    (receiving && countCheck.pending) ||
    invoiceNumberTooLong ||
    // TASK-0236: an invoice aimed at a PO is recorded there; "which PO?" needs an answer first
    // (a draft adds no stock, so the server does not ask it).
    aimedAt !== null ||
    (whichOrderOpen && !asDraft && confirmedNow.length > 0) ||
    // TASK-0242: a picked suggestion's unit and costs are still loading.
    refreshingCount > 0;

  // TASK-0244: someone else had this invoice open shortly before me, or opened it after me.
  const otherReviewer = ((): string | null => {
    const fresh = (iso: string | null | undefined) =>
      iso != null && Date.now() - Date.parse(iso) < REVIEWING_FRESH_MS;
    const row = inboxRow;
    if (
      myMarker &&
      row?.reviewingAt &&
      row.reviewingByUserId != null &&
      row.reviewingByUserId !== me &&
      Date.parse(row.reviewingAt) > Date.parse(myMarker.reviewingAt) &&
      fresh(row.reviewingAt)
    ) {
      return `${row.reviewingBy ?? 'Someone'} opened this invoice after you, ${relativeTime(row.reviewingAt)}.`;
    }
    return previousReviewerText(myMarker, me, (iso) => relativeTime(iso));
  })();

  const review = (
    <div className="@container min-w-0 flex-1 space-y-3">
      {dismissDialog}
      {staleConflict ? (
        <div className="border-warn/40 bg-warn-muted flex items-start gap-2 rounded border p-3">
          <AlertTriangle className="text-warn mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-ink text-xs font-medium">
              This import changed while you were reviewing it
            </div>
            <p className="text-2xs text-ink-muted mt-1 leading-relaxed">
              {staleConflict}
            </p>
            <p className="text-2xs text-ink-faint mt-1">
              Autosave is paused. Your edits are keyed to the parse you loaded, so saving
              them over the new one would attach them to different lines.
            </p>
            <Button
              size="xs"
              variant="primary"
              className="mt-2"
              onClick={() => {
                loadedFor.current = null;
                setStaleConflict(null);
                void record.refetch();
              }}
            >
              Reload this review
            </Button>
          </div>
        </div>
      ) : null}

      {otherReviewer ? (
        <Callout tone="info" icon={<Eye />} title={otherReviewer}>
          Two people working on the same invoice overwrite each other&rsquo;s changes.
          Check with them before you both go on.
        </Callout>
      ) : null}

      {confirmError ? (
        <div className="border-negative/40 bg-negative-muted rounded border p-3">
          <div className="text-ink text-xs font-medium">The import was not confirmed</div>
          <p className="text-2xs text-ink-muted mt-1 font-mono leading-relaxed whitespace-pre-wrap">
            {confirmError}
          </p>
          <p className="text-2xs text-ink-faint mt-1">
            Nothing was committed — no purchase order and no stock movement.
          </p>
        </div>
      ) : null}

      {recordedRefusal ? (
        <Callout
          tone="negative"
          icon={<AlertTriangle />}
          title={recordedRefusal.message}
          actions={
            <Button size="xs" variant="primary" asChild>
              <Link
                to="/purchasing/$id"
                params={{ id: String(recordedRefusal.poId) }}
                search={{ mode: 'invoices' as const }}
              >
                Open PO #{recordedRefusal.poId}
              </Link>
            </Button>
          }
        >
          <div className="space-y-1.5">
            <p>Nothing was confirmed.</p>
            {recordedRefusal.code === 'ALREADY_RECORDED_ON_ORDER' ? (
              <Checkbox
                checked={keyByDate}
                onCheckedChange={setKeyByDate}
                label={
                  <span className="text-xs">
                    This vendor reuses invoice numbers: key it by date (then confirm
                    again)
                  </span>
                }
              />
            ) : null}
          </div>
        </Callout>
      ) : null}

      {aimedAt !== null ? (
        <Callout
          tone="info"
          title={`This invoice is for PO #${aimedAt}`}
          actions={
            <>
              <Button size="xs" variant="primary" asChild>
                <Link
                  to="/purchasing/$id/invoice"
                  params={{ id: String(aimedAt) }}
                  search={{ importId, mode: 'scan' }}
                >
                  Open on PO #{aimedAt}
                </Link>
              </Button>
              <Button size="xs" loading={aim.isPending} onClick={() => aim.mutate(null)}>
                Not this PO
              </Button>
            </>
          }
        >
          Record it on that PO. Confirm here is off while it is aimed there.
        </Callout>
      ) : candidates.length > 0 ? (
        <WhichOrderStep
          vendorName={storedVendorName ?? dupVendorName}
          candidates={candidates}
          selectedPoId={selectedOrder}
          onSelect={setPickedOrder}
          differentChosen={differentChosen}
          onUseOrder={(poId) => aim.mutate(poId)}
          onDifferent={() =>
            setDifferentFrom(differentChosen ? null : candidates.map((c) => c.poId))
          }
          busy={aim.isPending}
          disabledReason={
            vendorId !== data.vendorId
              ? `Re-match to ${dupVendorName ?? 'the vendor you picked'} first.`
              : null
          }
        />
      ) : null}

      <Panel
        title={data.sourceFilename ?? `Import #${data.id}`}
        description={`${data.parserModel ?? 'parser'} · attempt ${num(data.parseAttempts)} · ${
          data.fileSizeBytes
            ? `${Math.round(data.fileSizeBytes / 1024)} KB`
            : 'unknown size'
        }`}
        actions={
          <>
            {dirty ? (
              <span className="text-2xs text-ink-faint">
                {saveDraft.isPending ? 'Saving draft…' : 'Unsaved'}
              </span>
            ) : (
              <span className="text-2xs text-ink-faint">Draft saved</span>
            )}
            {data.fileKind && !showFile ? (
              <Button
                size="xs"
                variant="ghost"
                icon={<FileText className="size-3" />}
                onClick={() => changeShowFile(true)}
              >
                Show invoice file
              </Button>
            ) : null}
            <Button
              size="xs"
              variant="ghost"
              loading={retry.isPending}
              disabled={heldBackFrom !== null}
              title={heldBackFrom !== null ? HELD_BACK_HINT : undefined}
              onClick={() => setReparseOpen(true)}
            >
              Re-parse as image
            </Button>
            {dismissButton}
          </>
        }
      >
        <div className="grid gap-3 @md:grid-cols-2 @3xl:grid-cols-4">
          <Field label="Vendor" required>
            <div className="flex gap-1.5">
              <VendorSelect
                size="sm"
                value={vendorId}
                vendors={vendors.data}
                loading={vendors.isPending}
                onChange={(next) => {
                  setVendorId(next);
                  setDirty(true);
                }}
              />
              <Button
                size="sm"
                variant="default"
                icon={<Search className="size-3.5" />}
                disabled={
                  vendorId === null || vendorId === data.vendorId || heldBackFrom !== null
                }
                loading={rematch.isPending}
                onClick={() => {
                  if (vendorId !== null) rematch.mutate(vendorId);
                }}
                title={
                  heldBackFrom !== null
                    ? HELD_BACK_HINT
                    : "Re-run product matching against this vendor's catalog"
                }
              >
                Re-match
              </Button>
            </div>
          </Field>
          <Field
            label="Invoice number"
            required
            error={
              invoiceNumberTooLong
                ? INVOICE_NUMBER_TOO_LONG_MESSAGE
                : invoiceNumberMissing
                  ? 'Type the number from the paper, or tick “No number on the paper”.'
                  : undefined
            }
          >
            <div className="space-y-1">
              <Input
                size="sm"
                mono
                aria-label="Invoice number"
                value={invoiceNumber}
                disabled={noNumber}
                placeholder={noNumber ? 'No number on the paper' : undefined}
                invalid={invoiceNumberTooLong || invoiceNumberMissing}
                onChange={(e) => {
                  setInvoiceNumber(e.currentTarget.value);
                  setDirty(true);
                }}
              />
              <Checkbox
                checked={noNumber}
                onCheckedChange={(checked) => {
                  setNoNumber(checked);
                  if (checked) {
                    setInvoiceNumber('');
                    setDirty(true);
                  }
                }}
                label={<span className="text-2xs">No number on the paper</span>}
              />
            </div>
          </Field>
          <Field label="Parser read">
            <div className="flex h-7 items-center gap-2">
              <Badge
                tone={
                  confidence === 'high'
                    ? 'positive'
                    : confidence === 'medium'
                      ? 'warn'
                      : 'negative'
                }
              >
                {confidence} confidence
              </Badge>
              {data.extractedJson?.vendorName ? (
                <span className="text-2xs text-ink-faint truncate">
                  “{data.extractedJson.vendorName}”
                </span>
              ) : null}
            </div>
          </Field>
          <Field
            label="Delivered on"
            hint={
              asDraft
                ? 'A draft receives nothing, so it never asks.'
                : 'From the invoice; change it to the day the goods came in.'
            }
            error={asDraft ? null : deliveredOnError}
          >
            <Input
              type="date"
              size="sm"
              mono
              aria-label="Delivered on"
              value={deliveredOn}
              max={today}
              invalid={!asDraft && deliveredOnError !== null}
              onChange={(e) => {
                setDeliveredOn(e.currentTarget.value);
                setServerQuestions(null);
              }}
            />
          </Field>
          <Field label="Create as">
            <div className="flex h-7 items-center">
              <Checkbox
                checked={asDraft}
                onCheckedChange={(checked) => {
                  setAsDraft(checked);
                  setDirty(true);
                }}
                label="Save without receiving (draft)"
              />
            </div>
          </Field>
          <Field label="Notes for the PO" className="@md:col-span-2 @3xl:col-span-4">
            <Textarea
              rows={2}
              value={notes}
              maxLength={2048}
              placeholder="Anything the receiver or the bookkeeper should know…"
              onChange={(e) => {
                setNotes(e.currentTarget.value);
                setDirty(true);
              }}
            />
          </Field>
        </div>
        {heldBackFrom !== null ? (
          <Callout tone="info" className="mt-3">
            {HELD_BACK_HINT}{' '}
            <button
              type="button"
              className="text-signal-ink hover:underline"
              onClick={() =>
                void navigate({ to: '/purchasing/import', search: { id: heldBackFrom } })
              }
            >
              Open import #{heldBackFrom}
            </button>
          </Callout>
        ) : null}
        <DuplicateInvoiceCallout
          check={dup}
          vendorName={dupVendorName}
          invoiceNumber={invoiceNumber}
          onStop={() => void navigate({ to: '/purchasing/import', search: {} })}
          note={
            heldBackFrom !== null
              ? `Part of this invoice was held back from import #${heldBackFrom}, so “More of the same invoice” is probably the answer.`
              : undefined
          }
          className="mt-3"
        />
      </Panel>

      <StatRow className="md:grid-cols-2 xl:grid-cols-2 @2xl:grid-cols-4">
        <Stat
          label="Lines"
          value={num(lines.length)}
          sub={`${assigned.length} matched${
            uncheckedGuesses.length > 0 ? `, ${uncheckedGuesses.length} guessed` : ''
          }`}
        />
        <Stat
          label="Unmatched"
          value={num(unmatched.length)}
          tone={unmatched.length > 0 ? 'warn' : undefined}
          sub="saved for later"
        />
        <Stat
          label="On this order"
          value={money(confirmedSubtotal)}
          sub={`${countLabel(confirmedNow.length, 'line')}${
            heldBackSubtotal > 0 ? ` · ${money(heldBackSubtotal)} held back` : ''
          }`}
        />
        <Stat
          label="Invoice says"
          value={
            totals.printedTotalCents !== null
              ? money(totals.printedTotalCents)
              : totals.printedSubtotalCents !== null
                ? money(totals.printedSubtotalCents)
                : '—'
          }
          tone={totals.verdict === 'off' ? 'warn' : undefined}
          sub={
            totals.printedTotalCents === null && totals.printedSubtotalCents !== null
              ? `subtotal · ${totalsSummary(totals)}`
              : totalsSummary(totals)
          }
        />
      </StatRow>
      {totalsExplanation(totals) !== '' ? (
        <p
          className={cn(
            'text-2xs -mt-2',
            totals.verdict === 'off' ? 'text-warn' : 'text-ink-faint'
          )}
        >
          {totalsExplanation(totals)}
        </p>
      ) : null}

      {unitMismatch.length > 0 ? (
        <div className="border-negative/40 bg-negative-muted text-2xs text-ink rounded border p-3">
          <div className="font-medium">
            {countLabel(unitMismatch.length, 'line')} disagree with the product's unit
          </div>
          <ul className="text-ink-muted mt-1 space-y-0.5">
            {unitMismatch.map((line) => (
              <li key={line.key}>
                {line.productName || line.description}: invoice says {line.unit}, the
                product is {line.productUnit}. Fix the product's “sold by weight” setting
                or pick a different product — the API rejects the whole import otherwise.
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {receiving && unansweredMessage(unanswered.length) ? (
        <div className="border-warn/40 bg-warn-muted text-2xs text-ink rounded border p-3">
          {unansweredMessage(unanswered.length)}
        </div>
      ) : null}

      {quantityProblems.length > 0 ? (
        <div className="border-negative/40 bg-negative-muted text-2xs text-ink rounded border p-3">
          <div className="font-medium">
            {countLabel(quantityProblems.length, 'quantity', 'quantities')} to fix before
            confirming
          </div>
          <ul className="text-ink-muted mt-1 space-y-0.5">
            {quantityProblems.map((problem, index) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
          {unreadable.length > 0 ? (
            <p className="text-ink-faint mt-1">
              The draft autosave waits until every quantity box reads.
            </p>
          ) : null}
        </div>
      ) : null}

      <Panel
        title={`Lines · ${countLabel(lines.length, 'line')}`}
        description="Confirm the lines that are ready now — anything left unchecked or unmatched is saved to a new import, not lost."
        bleed
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {folding.fold ? (
              <ChipGroup<'needs' | 'all'>
                value={view}
                onChange={chooseView}
                options={[
                  { value: 'needs', label: `Needs you (${num(needingLines.length)})` },
                  { value: 'all', label: `All (${num(lines.length)})` },
                ]}
              />
            ) : null}
            {bulkKeys.size > 0 ? (
              <Button size="xs" variant="default" onClick={() => setBulkDialogOpen(true)}>
                Create products for {countLabel(bulkKeys.size, 'line')}
              </Button>
            ) : null}
          </div>
        }
      >
        {unfoldedNote(folding) ? (
          <p className="border-line-soft text-2xs text-ink-muted border-b px-3 py-1.5">
            {unfoldedNote(folding)}
          </p>
        ) : null}
        {!showAll && shownLines.length === 0 ? (
          <div className="text-2xs text-ink-muted flex items-center gap-1.5 px-3 py-3">
            <CheckCircle2 className="text-positive size-3.5" aria-hidden />
            Nothing on this invoice needs you.
          </div>
        ) : null}
        <div className="divide-line divide-y">
          {shownLines.map((line) => {
            const needs = needsByKey.get(line.key) ?? [];
            return (
              <LineRow
                key={line.key}
                rowRef={(node) => {
                  if (node) lineRefs.current.set(line.key, node);
                  else lineRefs.current.delete(line.key);
                }}
                active={cursorKey === line.key}
                line={line}
                needs={needs}
                checked={!showAll && needs.length === 0}
                stockQuestion={questionByKey.get(line.key) ?? null}
                stockAnswer={answerOf(line.key)}
                onAddStockChange={(addStock) => {
                  const answerKey = answerKeyOf(line.key);
                  if (answerKey !== null)
                    setStockTicks((prev) => ({ ...prev, [answerKey]: addStock }));
                }}
                vendorId={vendorId}
                bulkSelected={bulkKeys.has(line.key)}
                refreshing={refreshing[line.key] !== undefined}
                onPatch={(next) => patch(line.key, next)}
                onPickSuggestion={(candidate) => pickSuggestion(line.key, candidate)}
                onCreateProduct={() => setCreateLineKey(line.key)}
                onToggleBulkSelect={(checked) =>
                  setBulkKeys((prev) => {
                    const next = new Set(prev);
                    if (checked) next.add(line.key);
                    else next.delete(line.key);
                    return next;
                  })
                }
              />
            );
          })}
        </div>
        {foldedCount > 0 ? (
          <div className="border-line bg-surface-sunken text-2xs text-ink-muted flex flex-wrap items-center gap-2 border-t px-3 py-2">
            <CheckCircle2 className="text-positive size-3.5" aria-hidden />
            <span className="min-w-0 flex-1">
              {countLabel(foldedCount, 'clean line')} folded: matched by barcode, item
              number or a person, cost within 5% of the cost on file, nothing to check.
              They are on this order.
            </span>
            <Button size="xs" variant="ghost" onClick={() => chooseView('all')}>
              Show all {num(lines.length)}
            </Button>
          </div>
        ) : null}
      </Panel>

      {/* TASK-0244: the Confirm bar stays at the bottom of the screen while the lines scroll. */}
      <div
        data-testid="confirm-bar"
        className="border-line bg-surface sticky bottom-0 z-10 -mx-1 rounded-t border px-3 py-2 shadow-[0_-4px_12px_rgb(0_0_0/0.18)]"
      >
        <div className="flex flex-col gap-1.5">
          {uncheckedGuesses.length > 0 ? (
            <div className="text-2xs text-warn">
              {countLabel(uncheckedGuesses.length, 'line')} matched by a guess{' '}
              {uncheckedGuesses.length === 1 ? 'is' : 'are'} not ticked. Check the product
              and tick Confirm now to put {uncheckedGuesses.length === 1 ? 'it' : 'them'}{' '}
              on this order.
            </div>
          ) : null}
          {refreshingCount > 0 ? (
            <div className="text-2xs text-ink-muted">
              Loading the picked product&rsquo;s unit and costs…
            </div>
          ) : null}
          {invoiceNumberMissing ? (
            <div className="text-2xs text-warn">
              Type the invoice number, or tick “No number on the paper”.
            </div>
          ) : null}
          {deferredNow.length > 0 ? (
            <div className="text-2xs text-ink-muted">
              Confirming {num(confirmedNow.length)} of {num(lines.length)} —{' '}
              {countLabel(deferredNow.length, 'line')} will be saved for later.
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-mono tabular-nums">
              {needingLines.length > 0 ? (
                <span className="text-warn mr-2 font-sans">
                  Needs you: {num(needingLines.length)}
                </span>
              ) : null}
              {countLabel(confirmedNow.length, 'line')} · {money(confirmedSubtotal)}
              {priceUpdates.length > 0
                ? ` · ${countLabel(priceUpdates.length, 'retail price')} to update`
                : ''}
            </span>
            <Button
              size="sm"
              variant="primary"
              icon={<Sparkles className="size-3.5" />}
              disabled={blocked}
              onClick={() => setConfirmOpen(true)}
            >
              {confirmedNow.length === 0
                ? `Save ${countLabel(deferredNow.length, 'line')} for later`
                : deferredNow.length === 0
                  ? asDraft
                    ? 'Create draft PO'
                    : 'Confirm & receive'
                  : `Confirm ${confirmedNow.length}, save ${deferredNow.length} for later`}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          confirmedNow.length === 0
            ? 'Save every line for later?'
            : asDraft
              ? 'Create a draft purchase order?'
              : 'Confirm and receive this invoice?'
        }
        confirmLabel={
          confirmedNow.length === 0
            ? 'Save for later'
            : asDraft
              ? 'Create draft'
              : 'Confirm & receive'
        }
        variant="primary"
        loading={confirm.isPending}
        confirmDisabled={confirmHeldBecause !== null}
        consequences={[
          confirmedNow.length > 0
            ? `Creates a purchase order with ${countLabel(confirmedNow.length, 'line')} worth ${money(
                confirmedSubtotal
              )}.`
            : 'Creates no purchase order — nothing is being confirmed right now.',
          ...(outOfStockNow.length > 0
            ? [
                `${countLabel(outOfStockNow.length, 'line')} shipped nothing (out of stock): on the order with what was ordered, nothing received.`,
              ]
            : []),
          ...(confirmedNow.length > 0
            ? [
                asDraft
                  ? 'Draft: nothing is received and no stock moves.'
                  : `Receives all of it — ${unitTotalsText(
                      totalsByUnit(readConfirmed, (line) => ({
                        unit: line.unit,
                        // A line left out as counted (C45) records cost only.
                        quantity: leftOutLines.some((left) => left.key === line.key)
                          ? 0
                          : submitPayloadForLine(line).qty,
                      }))
                    )} go on hand immediately.`,
                ...(!asDraft && countConfirmationConsequence(leftOutLines.length)
                  ? [countConfirmationConsequence(leftOutLines.length)!]
                  : []),
              ]
            : []),
          deferredNow.length > 0
            ? `${countLabel(deferredNow.length, 'line')} will be saved to a new import you can pick up later.`
            : 'Every parsed line is matched and included.',
          priceUpdates.length > 0
            ? `Then attempts ${countLabel(
                priceUpdates.length,
                'retail price update'
              )} — a failure there does not undo the PO.`
            : 'No retail prices change.',
          ...(dup.noteLine ? [`The order's notes will say “${dup.noteLine}”.`] : []),
          ...(noNumber && confirmedNow.length > 0
            ? ['Recorded with no invoice number (none on the paper).']
            : []),
          ...(!asDraft && differentChosen && differentFrom
            ? [
                `Recorded as a separate delivery from ${differentFrom
                  .map((id) => `PO #${id}`)
                  .join(', ')}.`,
              ]
            : []),
          ...(keyByDate
            ? [
                'Keyed by its date: the same number on another PO is not treated as this invoice.',
              ]
            : []),
        ]}
        onConfirm={() => confirm.mutate()}
      >
        {confirmHeldBecause ? (
          <p className="text-2xs text-warn">{confirmHeldBecause}</p>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={reparseOpen}
        onOpenChange={setReparseOpen}
        title="Read this invoice again?"
        description="Your review changes on this invoice will be cleared."
        confirmLabel="Read it again"
        variant="primary"
        loading={retry.isPending}
        consequences={[
          'The lines, products and quantities you see now are replaced by a fresh reading of the file.',
          'Each reading uses the AI service and costs a little.',
        ]}
        onConfirm={() => {
          setReparseOpen(false);
          retry.mutate(true);
        }}
      />

      <CreateProductDialog
        open={createLineKey !== null}
        vendorId={vendorId}
        line={lines.find((l) => l.key === createLineKey) ?? null}
        onOpenChange={(open) => {
          if (!open) setCreateLineKey(null);
        }}
        onCreated={(key, product) => attachCreatedProduct(key, product)}
      />

      <BulkCreateDialog
        open={bulkDialogOpen}
        vendorId={vendorId}
        lines={lines.filter((l) => bulkKeys.has(l.key))}
        onOpenChange={setBulkDialogOpen}
        onAttached={(key, product) => {
          attachCreatedProduct(key, product);
          setBulkKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }}
      />
    </div>
  );

  // The invoice file sits beside the review on a wide screen (above it on a narrow one).
  return (
    <div className="flex flex-col gap-3 p-4 min-[1440px]:flex-row min-[1440px]:items-start">
      {review}
      {data.fileKind && showFile ? (
        <InvoiceFilePane
          importId={data.id}
          fileKind={data.fileKind}
          filename={data.sourceFilename}
          uploadedBy={data.uploadedByName ?? null}
          uploadedAt={data.createdAt}
          onHide={() => changeShowFile(false)}
          className="order-first h-[24rem] w-full shrink-0 min-[1440px]:sticky min-[1440px]:top-4 min-[1440px]:order-last min-[1440px]:h-[calc(100vh-11rem)] min-[1440px]:w-[22rem] min-[1700px]:w-[28rem]"
        />
      ) : null}
    </div>
  );
}

/* ── One invoice line ──────────────────────────────────────────────────────── */

function LineRow({
  line,
  rowRef,
  active = false,
  needs = [],
  checked = false,
  stockQuestion,
  stockAnswer: answer,
  onAddStockChange,
  vendorId,
  bulkSelected,
  refreshing,
  onPatch,
  onPickSuggestion,
  onCreateProduct,
  onToggleBulkSelect,
}: {
  line: ReviewLine;
  /** TASK-0244: the row's element, so j / k can scroll it into view. */
  rowRef?: (node: HTMLDivElement | null) => void;
  /** TASK-0244: the j / k line. */
  active?: boolean;
  /** TASK-0244: why the line needs a person (empty when clean). */
  needs?: NeedsReason[];
  /** TASK-0244: shown on "Needs you" because it needed a person, and it no longer does. */
  checked?: boolean;
  /** TASK-0230: set when the item was counted on or after the delivery day. */
  stockQuestion: CountQuestion | null;
  /** The answer so far; null = not answered (a line delivered the day its item was counted). */
  stockAnswer: boolean | null;
  onAddStockChange: (addStock: boolean) => void;
  vendorId: number | null;
  /** TASK-0191: checked for the bulk "Create products for N lines" table. */
  bulkSelected: boolean;
  /** TASK-0242: the picked suggestion's product details are still loading. */
  refreshing: boolean;
  onPatch: (next: Partial<ReviewLine>) => void;
  /** TASK-0242: a suggestion chip was picked; the screen refreshes the line from that product. */
  onPickSuggestion: (candidate: MatchCandidate) => void;
  /** Opens the single-line inline "Create product" dialog for this line. */
  onCreateProduct: () => void;
  onToggleBulkSelect: (checked: boolean) => void;
}) {
  const [swapping, setSwapping] = useState(false);
  const read = readLine(line);
  const perEach = derivedPerEachCents(line);
  const invoiceCostPerEach = perEach != null ? Math.round(perEach) : line.unitCostCents;
  const suggested = keepMarginPriceCents(
    line.currentPriceCents,
    line.systemUnitCostCents,
    invoiceCostPerEach
  );
  const costMoved =
    line.systemUnitCostCents != null && line.systemUnitCostCents !== invoiceCostPerEach;
  // TASK-0244: the move the "Needs you" filter reads (5% or more), signed, for the badge.
  const move = needs.includes('price_moved')
    ? costMovePct({
        systemUnitCostCents: line.systemUnitCostCents,
        invoiceUnitCostCents: invoiceCostPerEach,
      })
    : null;
  const mismatch = line.productUnit !== null && line.productUnit !== line.unit;
  // TASK-0191 round 3 (review fix, finding 4): a matched line the operator
  // has unchecked is a DIFFERENT state from "still unmatched" — it has a
  // product, it's just not going on the PO this time — and used to render
  // identically to a plain confirming row beyond the small checkbox, easy
  // to miss on a long list. Gets its own muted tone plus a badge, distinct
  // from the unmatched warning tint.
  const heldBack = line.productId !== null && !line.included;
  // TASK-0242: a guess not ticked yet reads as a guess to check, not as a line held back.
  const guessToCheck = heldBack && isGuessedMatch(line);
  const outOfStock = isOutOfStockLine(line);

  return (
    <div
      ref={rowRef}
      data-line-key={line.key}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'scroll-mt-4 scroll-mb-28 p-3',
        line.productId === null && 'bg-warn-muted/40',
        heldBack && 'bg-neutral-chip/30',
        active && 'ring-signal ring-2 ring-inset'
      )}
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-ink truncate text-xs">
            {line.description || '(no description)'}
          </div>
          <div className="text-2xs text-ink-faint mt-0.5 flex flex-wrap items-center gap-1.5">
            {line.sku ? <Code>{line.sku}</Code> : null}
            {line.upc ? <Code>{line.upc}</Code> : null}
            {line.unitsPerCase ? <span>{num(line.unitsPerCase)} / case</span> : null}
            <ConfidenceBadge confidence={line.confidence} method={line.matchMethod} />
          </div>
        </div>
      </div>

      {/* Product assignment */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-2xs text-ink-faint tracking-wider uppercase">Product</span>
        {line.productId !== null ? (
          <span className="text-ink truncate text-xs">{line.productName}</span>
        ) : (
          <Badge tone="warn">No match — saved for later</Badge>
        )}
        {line.productId === null && line.inactiveMatch ? (
          <Badge tone="negative">
            {line.inactiveMatch.method === 'upc' ? 'Barcode' : 'Item number'} is on an
            inactive product: {line.inactiveMatch.productName}
          </Badge>
        ) : null}
        <Button size="xs" variant="ghost" onClick={() => setSwapping((s) => !s)}>
          {swapping ? 'Close' : line.productId === null ? 'Find a product' : 'Change'}
        </Button>
        {line.productId === null ? (
          <Button size="xs" variant="ghost" onClick={onCreateProduct}>
            Create product
          </Button>
        ) : (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onPatch({ productId: null, productName: '', included: false })}
          >
            Skip line
          </Button>
        )}
        {mismatch ? (
          <Badge tone="negative">
            unit {line.unit} vs product {line.productUnit}
          </Badge>
        ) : null}
        {guessToCheck ? (
          <Badge tone="warn">Guessed: check</Badge>
        ) : heldBack ? (
          <Badge tone="neutral">Held back</Badge>
        ) : null}
        {outOfStock ? <Badge tone="neutral">Out of stock on the invoice</Badge> : null}
        {needs.includes('parser_flag') ? (
          <Badge tone="warn">{NEEDS_REASON_LABEL.parser_flag}: see the note below</Badge>
        ) : null}
        {move !== null ? (
          <Badge tone="warn">
            {NEEDS_REASON_LABEL.price_moved} {move > 0 ? '+' : ''}
            {move.toFixed(1)}% vs the cost on file
          </Badge>
        ) : null}
        {checked ? (
          <Badge tone="positive" dot>
            Checked
          </Badge>
        ) : null}
        {refreshing ? (
          <span className="text-2xs text-ink-faint flex items-center gap-1">
            <Loader2 className="size-3 animate-spin" /> Loading its unit and costs…
          </span>
        ) : null}
        {line.productId !== null ? (
          <Checkbox
            checked={line.included}
            onCheckedChange={(checked) => onPatch({ included: checked })}
            label="Confirm now"
          />
        ) : (
          <Checkbox
            checked={bulkSelected}
            onCheckedChange={onToggleBulkSelect}
            label="Select for bulk create"
          />
        )}
      </div>

      <LearnNote line={line} onChoose={(skuChoice) => onPatch({ skuChoice })} />

      {stockQuestion ? (
        <div className="border-warn/35 bg-warn-muted mt-2 flex flex-wrap items-center gap-2 rounded border px-2 py-1.5">
          <Badge tone="warn">
            Last counted {storeDayLabel(stockQuestion.lastCountedOn)}
          </Badge>
          <span className="text-2xs text-ink min-w-0 flex-1">
            Stock note: counted {storeDayLabel(stockQuestion.lastCountedOn)},{' '}
            {time(stockQuestion.lastCountedAt)} on the{' '}
            {COUNT_SOURCE_LABEL[stockQuestion.countSource] ?? stockQuestion.countSource},{' '}
            {stockQuestion.sameDayAsCount ? 'the same day as' : 'after'} this delivery on{' '}
            {storeDayLabel(stockQuestion.receiptDate)}.{' '}
            {stockQuestion.sameDayAsCount
              ? 'Was it on the shelf when it was counted?'
              : 'Unticked records the quantity, cost and bill without adding stock again.'}
          </span>
          {stockQuestion.sameDayAsCount ? (
            <SameDayChoice
              line={{ name: line.productName || line.description }}
              answer={answer}
              onChange={onAddStockChange}
            />
          ) : (
            <Checkbox
              checked={answer === true}
              onCheckedChange={onAddStockChange}
              label={<span className="text-2xs">Add to stock</span>}
            />
          )}
        </div>
      ) : null}

      {/* TASK-0242: shown on a guessed or unmatched line with suggestions, and kept after a
          person's pick so the reviewer can switch to another one. */}
      {line.candidates.some(
        (candidate) => candidate.confidence !== 'high' || candidate.inactive
      ) &&
      (line.confidence !== 'high' || line.matchMethod === 'manual') ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-2xs text-ink-faint">Suggestions:</span>
          {line.candidates.slice(0, 4).map((candidate) => (
            <button
              key={candidate.productId}
              onClick={() => onPickSuggestion(candidate)}
              className={cn(
                'text-2xs rounded-sm border px-1.5 py-0.5',
                candidate.productId === line.productId
                  ? 'border-signal-line bg-signal-muted text-ink'
                  : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink'
              )}
            >
              {candidate.productName}
              <span className="text-ink-faint ml-1">
                {/* TASK-0243: an inactive product is offered, never preselected. */}
                {candidate.inactive ? 'inactive' : candidate.confidence}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {swapping ? (
        <div className="mt-2">
          <ProductPicker
            vendorId={vendorId}
            autoFocus
            placeholder="Search the catalog for this line…"
            onPick={(product) => {
              onPatch({
                productId: product.id,
                productName: product.name,
                productUnit: product.unit,
                currentPriceCents: product.price,
                systemUnitCostCents: product.vendorUnitCost ?? product.cost ?? null,
                confidence: 'high',
                matchMethod: 'manual',
                included: true,
                // A retail price typed for the previous product is not this one's.
                newPriceCents: null,
                // TASK-0243: this vendor's item number and pack for the picked product.
                vendorSku: product.vendorSku,
                linkPack: product.vendorUnitsPerCase,
                skuChoice: 'keep',
              });
              setSwapping(false);
            }}
          />
        </div>
      ) : null}

      {/* Quantities and cost */}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <Field label="Ordered" className="w-24">
          <QuantityInput
            size="sm"
            unit={boxUnit(line)}
            aria-label={`Ordered for ${line.description || `line ${line.key + 1}`}`}
            value={line.orderedQtyText}
            invalid={line.orderedQtyText.trim() === ''}
            onValueChange={(text) => onPatch({ orderedQtyText: text })}
          />
        </Field>
        <Field label={line.caseMode ? 'Cases in' : 'Received'} className="w-24">
          <QuantityInput
            size="sm"
            unit={boxUnit(line)}
            aria-label={`${line.caseMode ? 'Cases in' : 'Received'} for ${
              line.description || `line ${line.key + 1}`
            }`}
            value={line.qtyText}
            invalid={
              line.qtyText.trim() === '' ||
              (read !== null && read.qty === 0 && read.orderedQty === 0)
            }
            onValueChange={(text) => onPatch({ qtyText: text })}
          />
        </Field>
        <Field label={line.caseMode ? 'Cost / case' : 'Cost / unit'} className="w-28">
          <MoneyInput
            size="sm"
            value={line.unitCostCents}
            onValueChange={(cents) => onPatch({ unitCostCents: cents ?? 0 })}
          />
        </Field>
        <Field label="Unit" className="w-20">
          <Select
            size="sm"
            value={line.unit}
            onChange={(e) =>
              onPatch({ unit: e.currentTarget.value === 'lb' ? 'lb' : 'ea' })
            }
          >
            <option value="ea">ea</option>
            <option value="lb">lb</option>
          </Select>
        </Field>
        {(line.unitsPerCase ?? 0) > 1 ? (
          <div className="pb-1">
            <Checkbox
              checked={line.caseMode}
              label={`Case mode (${num(line.unitsPerCase ?? 0)}/cs)`}
              onCheckedChange={(checked) => onPatch({ caseMode: checked })}
            />
          </div>
        ) : null}
        <div className="ml-auto pb-1 text-right">
          <div className="text-ink font-mono text-sm tabular-nums">
            {read ? money(caseModeSubtotalCents(read)) : '—'}
          </div>
          {perEach !== null ? (
            <div className="text-2xs text-ink-faint font-mono tabular-nums">
              {money(Math.round(perEach))} / ea ·{' '}
              {read ? qty(read.qty * (line.unitsPerCase ?? 1)) : '—'} eaches
            </div>
          ) : null}
        </div>
      </div>

      {line.warnings.length > 0 ? (
        <div className="mt-2 space-y-1">
          <WarningList warnings={line.warnings} />
          {/* TASK-0244 review R1-1: a parser question keeps the line under "Needs you" until a
              person says they looked (saved with the review, never sent with the confirm). */}
          {actionableWarnings(line.warnings).length > 0 ? (
            <Checkbox
              checked={line.warningsAcked}
              onCheckedChange={(checked) => onPatch({ warningsAcked: checked })}
              label={<span className="text-2xs">I checked the note above</span>}
            />
          ) : null}
        </div>
      ) : null}

      {/* Price conflict */}
      {line.productId !== null && line.currentPriceCents !== null ? (
        <div
          className={cn(
            'mt-2 rounded border p-2',
            costMoved ? 'border-warn/30 bg-warn-muted' : 'border-line bg-surface-sunken'
          )}
        >
          <div className="text-2xs flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-ink-faint">
              System cost{' '}
              <span className="text-ink font-mono tabular-nums">
                {money(line.systemUnitCostCents)}
              </span>
            </span>
            <span className="text-ink-faint">
              Invoice cost{' '}
              <span
                className={cn(
                  'font-mono tabular-nums',
                  costMoved ? 'text-warn' : 'text-ink'
                )}
              >
                {money(invoiceCostPerEach)}
              </span>
            </span>
            <span className="text-ink-faint">
              Retail{' '}
              <span className="text-ink font-mono tabular-nums">
                {money(line.currentPriceCents)}
              </span>
            </span>
            {line.currentPriceCents > 0 ? (
              <span className="text-ink-faint">
                Margin now{' '}
                <span className="text-ink font-mono tabular-nums">
                  {(
                    ((line.currentPriceCents - invoiceCostPerEach) /
                      line.currentPriceCents) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </span>
            ) : null}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="text-2xs text-ink-faint tracking-wider uppercase">
              New retail
            </span>
            <MoneyInput
              size="sm"
              className="w-24"
              value={line.newPriceCents}
              onValueChange={(cents) => onPatch({ newPriceCents: cents })}
            />
            {suggested !== null ? (
              <Button
                size="xs"
                variant="default"
                onClick={() => onPatch({ newPriceCents: suggested })}
              >
                Keep current margin → {money(suggested)}
              </Button>
            ) : null}
            {line.newPriceCents !== null ? (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => onPatch({ newPriceCents: null })}
              >
                Leave retail alone
              </Button>
            ) : null}
            {line.newPriceCents !== null ? (
              <span className="text-2xs text-ink-faint">
                Applied after the PO is created, as a separate save.
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Bulk product creation (TASK-0191) ───────────────────────────────────────
   This vendor's catalog is often nearly all colour/size variants of a
   handful of base items — the incident this task fixes was 61 of them in
   one invoice. One table beats the single-line dialog run 61 times: name
   and cost are editable per row, everything else defaults from the line
   the same way the single dialog does.                                     */

type BulkRow = {
  key: number;
  name: string;
  price: number | null;
  cost: number | null;
  /** Display only; null while the line's box does not read. */
  qty: number | null;
  description: string;
};

function BulkCreateDialog({
  open,
  vendorId,
  lines,
  onOpenChange,
  onAttached,
}: {
  open: boolean;
  vendorId: number | null;
  /** The currently bulk-selected lines, in list order. */
  lines: ReviewLine[];
  onOpenChange: (open: boolean) => void;
  onAttached: (key: number, product: CreatedProduct) => void;
}) {
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(
      lines.map((line) => ({
        key: line.key,
        name: line.description,
        price: null,
        cost:
          derivedPerEachCents(line) ??
          (line.unitCostCents > 0 ? line.unitCostCents : null),
        qty: readLine(line)?.qty ?? null,
        description: line.description,
      }))
    );
    setErrors({});
    // Selection only changes while the dialog is closed (the trigger button
    // is hidden once it's open) — re-seed rows whenever it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patchRow = (key: number, next: Partial<BulkRow>) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...next } : row)));

  const canSubmit =
    rows.length > 0 &&
    rows.every((row) => row.name.trim() !== '' && row.price != null && row.price >= 0);

  const submit = async () => {
    setSubmitting(true);
    const byKey = new Map(lines.map((line) => [line.key, line]));
    const input: BulkCreateLine[] = rows.map((row) => {
      const line = byKey.get(row.key);
      return {
        key: row.key,
        name: row.name.trim(),
        price: row.price ?? 0,
        cost: row.cost,
        barcode: line?.upc || null,
        vendorSku: line?.sku || null,
        unitsPerCase: line?.unitsPerCase ?? null,
        caseCost: line?.caseMode ? line.unitCostCents : null,
        // Finding 1, bulk row-builder call site: same fix as the single
        // dialog — a `lb` line must create a weight-sold product.
        unit: line?.unit ?? 'ea',
      };
    });

    const results = await createProductsForLines(vendorId, input);
    setSubmitting(false);

    const nextErrors: Record<number, string> = {};
    let ok = 0;
    for (const result of results) {
      if (result.ok) {
        onAttached(result.key, result.product);
        ok += 1;
        if (result.linkWarning)
          toast.warning(`"${result.product.name}": ${result.linkWarning}`);
      } else {
        nextErrors[result.key] = result.error;
      }
    }

    const failed = results.length - ok;
    if (failed === 0) {
      toast.success(
        `Created ${countLabel(ok, 'product')} and attached them to their lines.`
      );
      onOpenChange(false);
      return;
    }
    // Partial failure: keep the dialog open with only the failed rows, so
    // the operator can fix and retry — a bad row never blocks the rest,
    // and it isn't silently dropped either.
    toast.warning(
      `Created ${ok} of ${results.length} products. Fix the rest below and try again.`
    );
    setRows((prev) => prev.filter((row) => nextErrors[row.key] !== undefined));
    setErrors(nextErrors);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Create products for ${countLabel(lines.length, 'line')}`}
      description="Name and cost are prefilled from each invoice line — set a retail price for each and save. Vendor SKU, case pack and barcode link automatically."
      width="xl"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            Create {countLabel(rows.length, 'product')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.key}
            className={cn(
              'border-line flex flex-wrap items-end gap-2 rounded border p-2',
              errors[row.key] && 'border-negative/50 bg-negative-muted'
            )}
          >
            <div className="min-w-40 flex-1">
              <Field label="Name">
                <Input
                  size="sm"
                  value={row.name}
                  onChange={(e) => patchRow(row.key, { name: e.currentTarget.value })}
                />
              </Field>
              {errors[row.key] ? (
                <p className="text-2xs text-negative mt-1">{errors[row.key]}</p>
              ) : null}
            </div>
            <Field label="Cost" className="w-24">
              <MoneyInput
                size="sm"
                value={row.cost}
                onValueChange={(cents) => patchRow(row.key, { cost: cents })}
              />
            </Field>
            <Field label="Retail price" required className="w-24">
              <MoneyInput
                size="sm"
                value={row.price}
                onValueChange={(cents) => patchRow(row.key, { price: cents })}
              />
            </Field>
            <span className="text-2xs text-ink-faint w-14 shrink-0 pb-1.5 text-right font-mono tabular-nums">
              {qty(row.qty)}
            </span>
          </div>
        ))}
        {rows.length === 0 ? (
          <p className="text-2xs text-ink-faint">Nothing left to create.</p>
        ) : null}
      </div>
    </Dialog>
  );
}
